/**
 * Вкладка «Мониторинг» (Б5.6). Макет — deliverables/ui-design-v1/build/bodies/Monitoring.html,
 * норма — 08-alerts.md §12 и 09 §9.5.
 *
 * ГЛАВНОЕ ПРАВИЛО ЭТОГО ЭКРАНА: не рисовать цифру, которой нет.
 *
 * Ноль, нарисованный на пустой выборке, читается как достижение — поэтому пустая карточка
 * показывает прочерк и причину, а пустая таблица — строку «данных пока нет», а не ноль и не
 * ошибку (08 §7: «компонент „нет данных за период“ на пустой выборке», ветвлений по фазе в UI
 * нет — меняется наполнение view).
 *
 * У этого правила есть ВТОРАЯ половина, и она — весь смысл экрана (DEV-PLAN Б5.6): доля,
 * ушедшая к 0% или к 100%, это СИГНАЛ, а не пустота. Отсев префильтра 0% означает сломанную
 * рубрику, которая пропускает всё дальше и платит за это глубокой стадией; 100% означает
 * рубрику, которая не пропускает ничего (llm-costs ПРАВИЛО 3.2). Доля чтений кэша 0% при
 * непустом числе вызовов — регресс кэширования (ПРАВИЛО 2.9), и он стоит десятикратного
 * счёта. Обе величины обязаны быть ВИДИМО отличимы от прочерка «мерить нечем»: числом на
 * карточке плюс красной подписью, а не пустотой. За различение отвечает бэкенд (доля на
 * пустом знаменателе приходит `null`, миграция 0006), здесь — только отображение.
 *
 * ЧИТАЕМ ТОЛЬКО `v_ops_*` через `GET /api/v1/ops/:view` (09 §1.15). Имена — из белого списка
 * `OPS_VIEWS` (services/backend/src/domain/ops.ts). Прямого доступа к таблицам у вкладки нет
 * и не должно быть: view — тот самый узкий контракт, из-за которого уточнения схемы в
 * Б2/Б4.5 не потребовали правки фронта иначе, чем добавлением карточки.
 *
 * ИСКЛЮЧЕНИЕ ИЗ «ТОЛЬКО v_ops_*» ОДНО — ИСТОЧНИКИ (решение Ильи 15.10.2026, миграция 0021).
 * «Живость источников» читает `GET /api/v1/sources?brand=` со схемой в контрактах
 * (`SourceControlsResponseSchema`): кроме здоровья, ей нужны вес и включённость в трёх слоях —
 * реестр, правка из интерфейса, действующее, — и автор правки. Во вьюхе им не место: вьюха
 * отвечает «как дела», а здесь человек ещё и меняет.
 *
 * ЗАПИСЕЙ У ЭКРАНА ДВЕ. Вес и включённость источника — `PATCH /api/v1/sources/:code` из
 * панели источника (`SourcePanel.tsx`). И отметка качества версии (`POST …/versions/:n/mark`, Б5.6).
 * Оценивается опубликованное, список приходит той же `v_ops_*` (`quality-marks`), лайк
 * уходит в базу эталонов не отсюда, а экспортом (`tools/knowledge.ts`): обратной записи в
 * `reference_example` нет и второй кнопки не заводится (05-seams §8.2).
 *
 * Периода у маршрута нет: представления агрегируют весь накопленный срок, и сегментный
 * переключатель «30/90 дней» из макета нарисовать было бы враньём. Так и написано в шапке.
 *
 * ПОРЯДОК БЛОКОВ — ТРЕБОВАНИЕ ЗАКАЗЧИКА 26.08 И ОДНОВРЕМЕННО СМЫСЛ ЭКРАНА: сначала то, что
 * требует действия (открытые алерты и просроченные ожидания), потом метрики канала, и только
 * потом операционные разрезы. Метрика показывает, как идут дела; алерт говорит, что дела уже
 * не идут, — и он не должен ждать, пока человек долистает до низа.
 *
 * ПОРЯДОК НЕ ПЕРЕВЁРНУТ, А «ТРЕБУЕТ ВНИМАНИЯ» УЖАТО (правка заказчика 07.09.2026: «метрики
 * канала не приоритизированы сверху», «то, что требует внимания, никак не сворачивается,
 * занимает длинный коридор»). Оба требования выполняются одновременно ровно одним способом:
 * решение 26.08 остаётся в силе, а верхний блок перестаёт занимать экран. Когда действовать
 * не по чему — это одна строка «Требует внимания — открытых алертов нет»; когда есть —
 * свёрнутый раздел, у которого в заголовке ВСЕГДА виден счётчик по важности. Свёрнутое
 * прячет подробности, но никогда не прячет ФАКТ проблемы: иначе сворачивание превратилось бы
 * в способ не увидеть аварию.
 *
 * СВОРАЧИВАНИЕ — ОДИН ПРИМИТИВ `Section` НА ВСЕ РАЗДЕЛЫ, состояние переживает перезагрузку
 * (localStorage, тот же защищённый хелпер, что у бэклога). Внутрь тела уезжают таблицы,
 * графики и длинные справки; KPI-карточки остаются СНАРУЖИ и видны всегда — они и есть ответ
 * на «как дела», ради которого экран открывают, и прятать их за шевроном значило бы оставить
 * на экране одни заголовки.
 *
 * ПОЛНОШИРИННЫХ ГРАФИКОВ ПОД МЕТРИКАМИ БОЛЬШЕ НЕТ (правка 07.09.2026). Они дублировали то,
 * что уже делает нажатие на карточку метрики: тот же ряд, но в окне, где он больше и есть
 * выбор диапазона. Компонент графика остался — его рисует окно.
 *
 * РАСХОД LLM — ОДНА ТАБЛИЦА С ПЕРЕКЛЮЧАТЕЛЕМ, А НЕ ДВЕ ПОДРЯД. Разрезов два, потому что
 * вопроса два: `v_ops_llm_daily` группирует по СТАДИЯМ («что ест бюджет»),
 * `v_ops_llm_cache_daily` — по СЕРВИСАМ («работает ли ещё кэш»; кэшируется общий ПРЕФИКС
 * промпта, а он заморожен на сервис, а не на стадию). Ни один не выводится из другого: у
 * первого нет колонки сервиса, у второго нет стадии, и соединение по (день, бренд) на
 * клиенте дало бы декартово произведение и выдумало деньги, которых не тратили. Поэтому
 * рисуется РОВНО ОДИН разрез, а переключатель и строка под ним говорят, почему их два.
 *
 * ЖИВОСТЬ ИСТОЧНИКОВ — СЕТКА ЯЧЕЕК, А НЕ ТАБЛИЦА В СЕМЬ КОЛОНОК. Двадцать одна строка, из
 * которых каждая может стать многострочной от одного стектрейса, отвечала на вопрос «что
 * сломалось» экраном текста. В сетке видно код источника, долю успехов, действующий вес и
 * «выкл.» у выключенного; проблемы стоят первыми, выключенные — в конце. Нажатие открывает
 * ПАНЕЛЬ ИСТОЧНИКА (с 15.10.2026, `SourcePanel.tsx`): подробность здоровья — включая ПОЛНЫЙ
 * текст последней ошибки — плюс вес и включённость. Текст ошибки из UI не убран и убран быть
 * не может: по runbook'у класс ошибки — первый шаг починки, и другого места, где оператор его
 * видит, нет. Выключенные источники в сетке есть намеренно: иначе включить их было бы негде.
 *
 * МЕТРИКИ КАНАЛА — ЭТО §10 ТЗ, И ПОЛОВИНЫ ИЗ НИХ У НАС НЕТ. Регулярность публикаций и доля
 * постов без доработки считаются по нашей базе (`v_ops_publish_weekly`, миграция 0007).
 * Просмотры, реакции, число подписчиков и экономия времени НЕ считаются ничем — перечень с
 * причинами приходит отдельной вьюхой (`v_ops_metrics_unavailable`), и карточка по нему
 * рисует прочерк с причиной. Ноль вместо прочерка был бы прямой ложью: «просмотров 0» и
 * «просмотры недоступны» — разные утверждения, и первое из них неверно.
 */

import {
  SOURCE_FETCH_OUTCOMES,
  type SourceControl,
  SourceControlsResponseSchema,
} from '@smm/contracts/schemas';
import { type JSX, type ReactNode, useCallback, useMemo, useState } from 'react';
import { ApiRequestError, post } from '../api/client.ts';
import { useLastGood } from '../api/useLastGood.ts';
import { useResource } from '../api/useResource.ts';
import { BrandTitle } from '../brand.tsx';
import { AlertsList, type AlertsSummary } from '../components/AlertsList.tsx';
import { plural, shortMsk } from '../components/format.ts';
import { formatLabel } from '../components/formatLabel.ts';
import { KpiCard, type KpiTone } from '../components/KpiCard.tsx';
import { Modal } from '../components/Modal.tsx';
import { MonitoringRunOpener } from '../components/MonitoringRunDialog.tsx';
import { Shell } from '../components/Shell.tsx';
import styles from './MonitoringPage.module.css';
import { SourcePanel, weightLabel } from './SourcePanel.tsx';

/*
 * Формы строк сняты с определений представлений в `migrations/` (v_ops_*) с одной
 * нормализацией бэкенда (`domain/ops.ts`): колонка-МОМЕНТ `*_at_ms` отдаётся полем без
 * суффикса и строкой ISO. Длительность (`avg_latency_ms`) остаётся миллисекундами — это не
 * момент. Деньги приходят целыми микродолларами (0003), перевод в доллары — здесь.
 * Схем в `@smm/contracts` на эти маршруты нет: контрактом служит сама вьюха.
 */

interface PublishDailyRow {
  readonly day: string | null;
  readonly brand_code: string | null;
  readonly attempts: number;
  readonly sent: number;
  readonly failed: number;
  readonly deviations: number;
  readonly avg_latency_ms: number | null;
}

interface WatchdogRow {
  readonly key: string;
  readonly label: string;
  readonly brand_code: string | null;
  readonly state: string;
  readonly last_ok_at: string | null;
  readonly expected_by: string | null;
  readonly period_sec: number;
  readonly grace_sec: number;
}

/**
 * Строка `v_ops_schedule` (0024): обещание расписания бренда — вид за местный день и его исход
 * (P2 ревизии 2026-10-18). Моменты окна приходят ISO-строками, день — местная дата бренда.
 */
interface ScheduleRow {
  readonly brand_code: string | null;
  readonly kind: string;
  readonly local_day: string;
  readonly due_at: string | null;
  readonly until_at: string | null;
  readonly state: string;
  readonly reason: string | null;
  readonly post_no: number | null;
}

/**
 * Строка `v_ops_http_errors_daily` (0024): ответы бэкенда ≥ 400 по маршруту и коду за сутки
 * UTC (P2 ревизии 2026-10-18). Бренда нет: ошибки HTTP — свойство контура, а не канала.
 */
interface HttpErrorRow {
  readonly day: string;
  readonly listener: string;
  readonly route: string;
  readonly status: number;
  readonly code: string;
  readonly count: number;
  readonly caller: string | null;
  readonly last_at: string | null;
  readonly last_request_id: string | null;
}

/**
 * Строка `v_ops_source_fetch_recent` (0017) — ЖУРНАЛ попыток выборки, последние 200, потолок
 * запечён во вьюху. Фильтрация по источнику здесь клиентская и это осознанно: у маршрута
 * `GET /api/v1/ops/:view` параметров нет по норме §6.1, а двести строк — это меньше, чем
 * весит одна отрисовка сетки.
 *
 * Момент приходит нормализованным полем `started_at` (ISO), как у остальных вьюх. Поле
 * `started_at_ms` описано рядом ПОТОМУ, что регистрация вьюхи в белом списке бэкенда — правка
 * кода, а не схемы, и она приезжает отдельно: пока в её `instants` не внесли колонку, момент
 * придёт числом миллисекунд. Читаем оба вида и не показываем «Invalid Date» ни в одном.
 */
interface SourceFetchRow {
  readonly source_code: string;
  readonly parser: string | null;
  readonly purpose: string;
  readonly outcome: string;
  readonly http_status: number | null;
  readonly error_text: string | null;
  readonly redirect_location: string | null;
  readonly items_found: number | null;
  readonly items_new: number | null;
  readonly started_at?: string | null;
  readonly started_at_ms?: number | null;
  readonly attempt_no: number | null;
}

/**
 * Строка `v_ops_runs` (0004): `job_run` LEFT JOIN `monitoring_run`, порядок и потолок 200
 * запечены во вьюху. Счётчики прогона приходят только у прогонов мониторинга — у публикации
 * и обслуживания их нет, и это `null`, а не ноль.
 *
 * Колонок «мёртвых» и «деградирующих» источников во вьюхе НЕТ: прогон знает `sources_ok` из
 * `sources_total`, а вердикт здоровья ставится по окну последних десяти попыток и живёт на
 * `v_ops_source_health`. Поэтому здесь показано «N из M», а на вопрос «кто именно» отвечает
 * сетка живости выше — второй, расходящейся с ней оценки на экране не заводим.
 */
interface RunRow {
  readonly id: string;
  readonly kind: string;
  readonly brand_code: string | null;
  readonly started_at: string | null;
  readonly finished_at: string | null;
  readonly state: string;
  readonly sources_total: number | null;
  readonly sources_ok: number | null;
  readonly materials_seen: number | null;
  readonly materials_passed_heuristic: number | null;
  readonly materials_passed_llm: number | null;
  readonly topics_created: number | null;
  readonly error_text: string | null;
}

interface AlertDeliveryRow {
  readonly day: string | null;
  readonly transport: string;
  readonly deliveries: number;
  readonly sent: number;
  readonly failed: number;
}

interface LlmDailyRow {
  readonly day: string | null;
  readonly brand_code: string | null;
  readonly stage: string | null;
  readonly calls: number;
  readonly input_tokens: number;
  readonly cache_read_tokens: number;
  readonly output_tokens: number;
  readonly cost_total_micro_usd: number;
}

interface LlmCacheRow {
  readonly day: string | null;
  readonly brand_code: string | null;
  readonly service: string;
  readonly calls: number;
  readonly calls_with_cache_read: number;
  readonly cache_read_tokens: number;
  readonly prompt_tokens: number;
  readonly cache_read_ratio: number | null;
}

interface PrefilterRow {
  readonly day: string | null;
  readonly brand_code: string | null;
  readonly runs: number;
  readonly materials_seen: number;
  readonly passed_heuristic: number;
  readonly passed_llm: number;
  readonly topics_created: number;
  readonly heuristic_drop_ratio: number | null;
  readonly prefilter_drop_ratio: number | null;
}

interface DeepCostRow {
  readonly day: string | null;
  readonly brand_code: string | null;
  readonly deep_items: number | null;
  readonly prefilter_micro_usd: number | null;
  readonly deep_micro_usd: number | null;
  readonly monitor_micro_usd: number | null;
  readonly deep_micro_usd_per_item: number | null;
}

interface QualityDailyRow {
  readonly day: string | null;
  readonly brand_code: string | null;
  readonly approvals: number;
  readonly first_pass: number;
  readonly after_rework: number;
  readonly first_pass_ratio: number | null;
  readonly likes: number;
  readonly dislikes: number;
}

/**
 * Недельный разрез публикаций (0007). Единица счёта — работа публикации с номером
 * сообщения, то есть пост, физически лежащий в канале. `week_start` — локальная дата
 * понедельника, а НЕ момент: в `instants` бэкенда её нет намеренно, как и `day` у суточных
 * вьюх, и разбирать её здесь нечем и незачем.
 */
interface PublishWeeklyRow {
  readonly week_start: string | null;
  readonly brand_code: string | null;
  readonly published: number;
  readonly no_rework: number;
  readonly no_human_edit: number;
  readonly hands_off: number;
  readonly retracted: number;
  readonly no_rework_ratio: number | null;
  readonly hands_off_ratio: number | null;
}

/**
 * Строка `v_ops_channel_metrics_weekly` (0012). Три метрики с разной природой в одной
 * строке: `subscribers` — ЗАПАС на конец недели, `reactions`/`comments` — счётчики постов
 * ТОЙ недели. `null` у любого из трёх значит «на этой неделе не замеряли», и это не ноль.
 */
interface ChannelMetricsWeeklyRow {
  readonly week_start: string | null;
  readonly brand_code: string | null;
  readonly posts: number;
  readonly subscribers: number | null;
  readonly reactions: number | null;
  readonly reaction_posts: number;
  readonly comments: number | null;
  readonly comment_posts: number;
}

/**
 * Строка `v_ops_visual_share` (0015). Окно — ПОСЛЕДНИЕ 20 версий от сервиса генерации по
 * бренду, а не сутки: доля картинок меряется по постам, а постов в сутках два.
 *
 * Колонка называется `brand`, а не `brand_code`, как у остальных вьюх, — так её объявляет
 * миграция, и переименовывать её по дороге значило бы завести второе имя одному столбцу.
 */
interface VisualShareRow {
  readonly brand: string | null;
  readonly versions: number;
  readonly drawn: number;
  readonly share: number | null;
}

/** Метрика §10 ТЗ, у которой нет источника данных, и код причины (0007, 0012). */
interface MetricUnavailableRow {
  readonly brand_code: string | null;
  readonly metric: string;
  readonly reason: string;
}

interface QualityMarkRow {
  readonly draft_id: string;
  readonly version_no: number;
  readonly brand_code: string | null;
  readonly status: string;
  readonly format_code: string | null;
  readonly created_at: string | null;
  readonly excerpt: string;
  readonly likes: number;
  readonly dislikes: number;
  readonly marked_at: string | null;
}

/** Разряды у крупных чисел — оформление, а не разбор: часов и зон здесь нет. */
const fmtInt = (n: number): string => n.toLocaleString('ru-RU');

const fmtMs = (ms: number | null): string => (ms === null ? '—' : `${(ms / 1000).toFixed(1)} с`);

/**
 * Момент — в зоне показа интерфейса (`DISPLAY_TZ` в `components/format.ts`) и с подписью
 * единицы. Своей нарезки времени у экрана нет: до 08.09.2026 её держал `AlertsList`, и
 * одно и то же событие в шапке и в списке подписывалось по-разному.
 */
const orDash = (iso: string | null): string => (iso === null ? '—' : shortMsk(iso));

/**
 * Доля наружу процентом. `null` — знаменателя не было, и это НЕ ноль: возвращается `null`,
 * а не «0 %», чтобы карточка нарисовала прочерк.
 */
const fmtPct = (r: number | null): string | null =>
  r === null ? null : `${(r * 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} %`;

/** Микродоллары → доллары. Точка, а не запятая: это сумма в USD, и разделитель у неё свой. */
const fmtUsd = (micro: number | null): string | null =>
  micro === null ? null : `$${(micro / 1_000_000).toFixed(2)}`;

/** Цена одного материала — четыре знака: на двух вся глубокая стадия читается как $0.00. */
const fmtUsdFine = (micro: number | null): string | null =>
  micro === null ? null : `$${(micro / 1_000_000).toFixed(4)}`;

interface StateView {
  readonly label: string;
  readonly dot: string;
  readonly color: string;
}

/** `watchdog_expectation.state` — CHECK (unknown|ok|overdue|disabled). */
const WATCHDOG_STATE: ReadonlyMap<string, StateView> = new Map<string, StateView>([
  ['ok', { label: 'в норме', dot: 'dot-ok', color: 'var(--ink-2)' }],
  ['overdue', { label: 'просрочен', dot: 'dot-err', color: 'var(--red-ink)' }],
  ['unknown', { label: 'ещё не отчитывался', dot: '', color: 'var(--muted)' }],
  ['disabled', { label: 'выключен', dot: '', color: 'var(--muted)' }],
]);

/** `source.health` — CHECK (unknown|ok|degraded|dead). */
const SOURCE_STATE: ReadonlyMap<string, StateView> = new Map<string, StateView>([
  ['ok', { label: 'работает', dot: 'dot-ok', color: 'var(--ink-2)' }],
  ['degraded', { label: 'деградирует', dot: 'dot-warn', color: 'var(--amber-ink)' }],
  ['dead', { label: 'мёртв', dot: 'dot-err', color: 'var(--red-ink)' }],
  ['unknown', { label: 'ещё не опрашивался', dot: '', color: 'var(--muted)' }],
]);

const UNKNOWN_STATE: StateView = { label: 'состояние неизвестно', dot: '', color: 'var(--muted)' };

/**
 * Выход сильнее «работает» (ревью 18.10.2026, №3). OpenAI News был «работает, 100 % из 10»
 * и не дал ни одного материала: здоровье судит ленту, а статьи за Cloudflare и тизеры по 150
 * знаков в нём не видны. Источник, чья лента отвечает, а выхода нет, рисуется деградирующим
 * и подписан словом выхода. Мёртвый и деградирующий по ленте — остаются своими словами: там
 * чинить сначала ленту.
 */
const YIELD_VIEW: ReadonlyMap<string, StateView> = new Map<string, StateView>([
  ['none', { label: 'не даёт материалов', dot: 'dot-warn', color: 'var(--amber-ink)' }],
  ['low', { label: 'статьи не скачиваются', dot: 'dot-warn', color: 'var(--amber-ink)' }],
]);

interface CellState {
  readonly state: string;
  readonly view: StateView;
  /** Подсвечено выходом, а не здоровьем ленты. */
  readonly byYield: boolean;
}

function cellStateOf(r: SourceControl): CellState {
  const health = r.health.state;
  const byYield = YIELD_VIEW.get(r.yield.state);
  if (byYield !== undefined && (health === 'ok' || health === 'unknown')) {
    return { state: 'degraded', view: byYield, byYield: true };
  }
  return { state: health, view: SOURCE_STATE.get(health) ?? UNKNOWN_STATE, byYield: false };
}

/** «выход 3 из 20» — материалов, прошедших порог длины, из впервые увиденных за окно. */
function yieldCellText(r: SourceControl): string | null {
  const y = r.yield;
  if (y.materials === 0 && y.articles_tried === 0) return null;
  return `выход ${fmtInt(Math.max(0, y.materials - y.too_short))} из ${fmtInt(y.materials)}`;
}

/** `schedule_occurrence.state` — CHECK 0024. Слова — про пост, а не про таблицу. */
const SCHEDULE_STATE: ReadonlyMap<string, StateView> = new Map<string, StateView>([
  ['pending', { label: 'ждёт очереди', dot: 'dot-warn', color: 'var(--amber-ink)' }],
  ['enqueued', { label: 'мониторинг ищет тему', dot: '', color: 'var(--ink-2)' }],
  ['topic_created', { label: 'тема есть, ждём пост', dot: '', color: 'var(--ink-2)' }],
  ['draft_ready', { label: 'пост готов', dot: 'dot-ok', color: 'var(--ink-2)' }],
  ['missed', { label: 'заявка не положена', dot: 'dot-err', color: 'var(--red-ink)' }],
  ['request_failed', { label: 'мониторинг без темы', dot: 'dot-err', color: 'var(--red-ink)' }],
  ['write_failed', { label: 'пост не написан', dot: 'dot-err', color: 'var(--red-ink)' }],
  ['skipped', { label: 'не требовался', dot: '', color: 'var(--muted)' }],
]);

const SCHEDULE_FAILED: ReadonlySet<string> = new Set(['missed', 'request_failed', 'write_failed']);

const SCHEDULE_KIND: Readonly<Record<string, string>> = { news: 'Новость', digest: 'Дайджест' };

/**
 * Причина обещания словами. Бэкенд пишет коды (`queue_busy:<заявка>`, `not_running`,
 * `writer_no_draft`, `not_enqueued:…`, `digest_exists:<тема>`); текст отказа заявки мониторинга
 * приходит уже словами и показывается как есть.
 */
export function scheduleReasonText(reason: string | null): string {
  if (reason === null || reason === '') return '—';
  if (reason.startsWith('queue_busy')) return 'очередь мониторинга была занята другой заявкой';
  if (reason === 'not_running') return 'бэкенд не работал всё окно';
  if (reason === 'writer_no_draft') return 'сервис генерации не написал пост за час';
  if (reason.startsWith('not_enqueued')) return 'тик расписания отказывал — см. журнал бэкенда';
  if (reason.startsWith('digest_exists')) return 'дайджест недели уже есть';
  return reason;
}

function StateCell({ view }: { view: StateView }): JSX.Element {
  return (
    <span className={styles['stateCell']}>
      <span
        className={view.dot === '' ? 'dot' : `dot ${view.dot}`}
        style={view.dot === '' ? { background: 'var(--muted)' } : undefined}
      />
      <span className="small" style={{ color: view.color }}>
        {view.label}
      </span>
    </span>
  );
}

/**
 * Три состояния выборки одним компонентом: грузим / не загрузилось / пусто. Возвращает null,
 * когда рисовать надо сами данные. Пустая выборка — это `val-info`, а не `val-err`: отсутствие
 * строк до первого прогона нормально и ожидаемо, красная плашка научила бы игнорировать
 * красные плашки.
 */
function DataState({
  loading,
  error,
  empty,
  emptyText,
}: {
  readonly loading: boolean;
  readonly error: Error | undefined;
  readonly empty: boolean;
  readonly emptyText: string;
}): JSX.Element | null {
  if (loading) return <div className="micro">Загружаем…</div>;
  if (error !== undefined) {
    return (
      <div className="val val-err">
        <span>Представление не загрузилось: {error.message}</span>
      </div>
    );
  }
  if (empty) {
    return (
      <div className="val val-info">
        <span>{emptyText}</span>
      </div>
    );
  }
  return null;
}

/* ══ сворачиваемый раздел ═════════════════════════════════════════════════════ */

/**
 * Ключ состояния раздела. Один префикс на экран, идентификатор раздела — суффикс: так в
 * хранилище видно, чьи это записи, и они не путаются с ключом свёрнутого бэклога.
 */
const sectionKey = (id: string): string => `smm.monitoring.section.${id}`;

/**
 * ФОРМА ХЕЛПЕРА ПОВТОРЕНА С `BacklogAside` ДОСЛОВНО И НАМЕРЕННО (хранилище берётся у
 * `window`, а не у `globalThis`; обе операции в try/catch). Причина та же и она не
 * стилистическая: в Node 26 есть СВОЙ глобальный `localStorage`, недоступный без флага
 * `--localstorage-file`, и под vitest он перекрывает хранилище jsdom — обращение через
 * `globalThis` молча возвращает `undefined`. Доступ бросает в приватном режиме Safari и при
 * запрете сторонних данных, а свёрнутость — удобство, а не состояние документа: падать
 * из-за неё экран права не имеет.
 *
 * Второго хелпера в пакете быть не должно; общего дома у него пока нет, поэтому копия —
 * ровно та же, а не «похожая»: разойдясь, они разойдутся молча.
 */
function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** `null` — человек этот раздел ещё не трогал, и решает умолчание вызывающего. */
function readSection(id: string): boolean | null {
  try {
    const v = storage()?.getItem(sectionKey(id));
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

function writeSection(id: string, open: boolean): void {
  try {
    storage()?.setItem(sectionKey(id), open ? '1' : '0');
  } catch {
    /* см. storage(): молчим намеренно */
  }
}

interface SectionProps {
  /** Идентификатор состояния в хранилище; он же основа id тела для `aria-controls`. */
  readonly id: string;
  readonly title: string;
  /** Строка справа в заголовке: как правило, имя вьюхи-источника. */
  readonly meta?: string;
  /**
   * Счётчик, который видно и в СВЁРНУТОМ виде: «2 критично · 1 предупреждение», «3 мёртвых».
   * Свёрнутое прячет подробности, но не факт проблемы, — бейдж и есть этот факт.
   */
  readonly badge?: string | null;
  /** Бейдж красный (проблема) или нейтральный (просто счётчик). */
  readonly alarm?: boolean;
  /**
   * Умолчание, пока человек раздел не трогал. `null` — «ещё не решено» (данных, по которым
   * считается умолчание, пока нет): раздел показывается свёрнутым и раскроется сам, когда
   * умолчание станет известно.
   */
  readonly defaultOpen?: boolean | null;
  /**
   * KPI-карточки. Стоят СНАРУЖИ тела и видны всегда, даже когда раздел свёрнут: карточка —
   * это и есть ответ на «как дела», ради которого экран открывают. Свернуть её значило бы
   * оставить на экране один заголовок.
   */
  readonly cards?: ReactNode;
  /** Одна строка под карточками, тоже вне тела: состояние выборки или предупреждение. */
  readonly aside?: ReactNode;
  /**
   * Ровная строка вместо заголовка-кнопки: разворачивать нечего. Так выглядит «Требует
   * внимания», когда действовать не по чему, — одна строка вместо карточки с абзацем.
   */
  readonly calm?: boolean;
  readonly calmNote?: string;
  readonly children?: ReactNode;
}

/**
 * ЕДИНСТВЕННЫЙ ПРИМИТИВ СВОРАЧИВАНИЯ ЭКРАНА. Заголовок — кнопка с `aria-expanded`, шеврон
 * той же геометрии, что у сворачивания бэклога (одна фигура раскрытия на всё приложение),
 * бейдж и тело.
 *
 * ТЕЛО НЕ РАЗМОНТИРУЕТСЯ, а получает `hidden`. Причины две, и обе практические: данные
 * грузит страница, а не тело, — размонтирование не экономит ни одного запроса; а состояние
 * внутри тела (выбранный источник, выбранный разрез расхода) должно переживать сворачивание,
 * иначе сворачивание работало бы как «сбросить». Это же делает `<details>` в браузере.
 *
 * Заголовок обязан оставаться элементом с классом `h3`: порядок блоков экрана — предмет
 * решения заказчика, и он проверяется сбором `.h3` в порядке разметки.
 */
function Section({
  id,
  title,
  meta,
  badge = null,
  alarm = false,
  defaultOpen = false,
  cards,
  aside,
  calm = false,
  calmNote,
  children,
}: SectionProps): JSX.Element {
  /** `null` — человек кнопку не нажимал; тогда работает умолчание вызывающего. */
  const [chosen, setChosen] = useState<boolean | null>(() => readSection(id));
  const open = !calm && (chosen ?? defaultOpen ?? false);
  const bodyId = `mon-${id}-body`;

  return (
    <section className={styles['section']}>
      {calm ? (
        <div className={styles['calmRow']}>
          <span className="h3">{title}</span>
          <span className="micro">{calmNote ?? ''}</span>
          <span style={{ flex: 1 }} />
          {meta === undefined ? null : <span className="meta">{meta}</span>}
        </div>
      ) : (
        <button
          type="button"
          className={styles['sectionToggle']}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => {
            // Считается от ПОКАЗАННОГО состояния, а не от предыдущего выбора: пока человек
            // раздел не трогал, показанное задаёт умолчание, и `prev` тут был бы `null`.
            writeSection(id, !open);
            setChosen(!open);
          }}
        >
          {/* Шеврон смотрит туда, куда поедет тело: вправо — раскроется, вниз — уже раскрыт.
              Геометрия — та же, что у сворачивания бэклога: фигура раскрытия одна на всё
              приложение, и вторая читалась бы как другое действие. */}
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path
              d={open ? 'M3 6l5 5 5-5' : 'M6 3l5 5-5 5'}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="h3">{title}</span>
          {badge === null || badge === '' ? null : (
            <span
              className={alarm ? `${styles['badge']} ${styles['badgeAlarm']}` : styles['badge']}
            >
              {badge}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {meta === undefined ? null : <span className="meta">{meta}</span>}
        </button>
      )}

      {cards}
      {aside}

      <div className={styles['sectionBody']} id={bodyId} hidden={!open}>
        {children}
      </div>
    </section>
  );
}

/**
 * Подпись под долей, вырождение которой является отказом. Возвращает пару «текст, тон» —
 * ровно то, чем карточка отличает 0% как аварию от 0% как «ничего не мерили»: прочерк даёт
 * `KpiCard` при `value === null`, а здесь решается, что написать рядом с настоящим числом.
 */
function ratioNote(
  ratio: number | null,
  emptyNote: string,
  low: string,
  high: string,
  normal: string,
): { note: string; tone: 'down' | 'muted' } {
  if (ratio === null) return { note: emptyNote, tone: 'muted' };
  if (ratio <= 0) return { note: low, tone: 'down' };
  if (ratio >= 1) return { note: high, tone: 'down' };
  return { note: normal, tone: 'muted' };
}

/**
 * Порог тревоги по доле постов с картинкой — §2.6 плана. Живёт пока только здесь: второго
 * дома у числа нет. В день, когда бэкенд начнёт поднимать алерт по той же доле, порогов
 * станет два, и разойдутся они молча — тогда константа обязана переехать в `@smm/contracts`.
 */
const VISUAL_SHARE_ALARM = 0.5;

/**
 * Подпись под долей картинок — ДВУСТОРОННЯЯ тревога, и это не перестраховка.
 *
 * Верхняя сторона: доля выше порога означает, что механизм отказа перестал отказывать. Ради
 * этого различения вьюха и написана — обоснование у каждой отдельной картинки всегда звучит
 * правдоподобно, а 100% не звучит никак.
 *
 * Нижняя сторона: ноль на НЕПУСТОЙ выборке — это не скромность, а признак того, что вердикт
 * не доезжает до генерации. Ровно то различение «ноль как авария против ноля как пустоты»,
 * ради которого на этом экране уже написана `ratioNote`.
 */
function visualShareNote(
  share: number | null,
  versions: number,
): { note: string; tone: 'down' | 'muted' } {
  if (share === null) {
    return { note: 'вердиктов визуала ещё не было', tone: 'muted' };
  }
  if (share >= VISUAL_SHARE_ALARM) {
    return {
      note: `выше порога ${String(Math.round(VISUAL_SHARE_ALARM * 100))} %: механизм перестал отказывать — это повод разбираться, а не радоваться`,
      tone: 'down',
    };
  }
  if (share <= 0 && versions >= 10) {
    return {
      note: `ни одной картинки за ${String(versions)} вердиктов — проверьте, доходит ли вердикт до генерации`,
      tone: 'down',
    };
  }
  return { note: 'за последние 20 версий от сервиса генерации', tone: 'muted' };
}

/** Сумма поля по строкам, где оно не `null`; `has` отличает «сложили нули» от «нечего складывать». */
function sumOf<T>(
  rows: readonly T[],
  pick: (r: T) => number | null,
): { sum: number; has: boolean } {
  let sum = 0;
  let has = false;
  for (const r of rows) {
    const v = pick(r);
    if (v === null) continue;
    sum += v;
    has = true;
  }
  return { sum, has };
}

// ─────────────────────────────────────────────────────────────────────────────
// Метрики канала (§10 ТЗ)

/** Локальная дата 'YYYY-MM-DD' → «24.08». Строка режется, а не разбирается: R11. */
export function weekShort(week: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(week) ? `${week.slice(8, 10)}.${week.slice(5, 7)}` : week;
}

const fmtPct0 = (r: number): string =>
  `${(r * 100).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} %`;

/**
 * Изменение к прошлой неделе — та самая «стрелочка вверх-вниз». Стрелка ставится ТОЛЬКО
 * когда есть с чем сравнивать: у первой недели предыдущей не существует, и рисовать ей
 * «↑ +3» значило бы сравнить с нулём, которого никто не измерял.
 *
 * `unit === 'pp'` — процентные пункты: разница двух долей измеряется пунктами, а не
 * процентами, и «выросло на 12 %» про долю — другое утверждение.
 */
export function weekDelta(
  current: number | null,
  previous: number | null,
  unit: 'posts' | 'pp',
): { readonly text: string; readonly tone: KpiTone } {
  if (current === null) return { text: 'считать не из чего', tone: 'muted' };
  if (previous === null) return { text: 'первая в отчёте', tone: 'muted' };
  const diff = unit === 'pp' ? Math.round((current - previous) * 100) : current - previous;
  if (diff === 0) return { text: 'как неделей раньше', tone: 'muted' };
  const size = unit === 'pp' ? `${String(Math.abs(diff))} п.п.` : String(Math.abs(diff));
  return diff > 0
    ? { text: `↑ +${size} к прошлой`, tone: 'up' }
    : { text: `↓ −${size} к прошлой`, tone: 'down' };
}

interface MetricPoint {
  readonly week: string;
  /** `null` — недели в отчёте нет; столбца не будет, вместо значения прочерк. */
  readonly value: number | null;
  /** Из чего число собрано: «3 из 4». Показывается в таблице окна, не на карточке. */
  readonly caption: string;
}

interface ChannelMetric {
  readonly id: string;
  readonly label: string;
  readonly value: string | null;
  readonly note: string;
  readonly tone: KpiTone;
  /** Одна строка о том, как метрика считается: без неё доля — это цифра без определения. */
  readonly explain: string;
  /** Итог за весь срок — контекст для недельного значения на карточке. */
  readonly total: string;
  /**
   * ВСЕ недели отчёта, по возрастанию. Резать ряд — забота того, кто рисует: в плавающем
   * окне это выбранный диапазон. Храни мы здесь уже обрезанный ряд, окно не смогло бы
   * показать год.
   */
  readonly points: readonly MetricPoint[];
  readonly format: (v: number) => string;
  /**
   * Нижняя граница шкалы. У долей это 1 — сто процентов, и шкала обязана быть полной,
   * иначе 3 % рисуются во всю высоту. У счётчиков это тоже 1, но по другой причине: пустой
   * ряд иначе дал бы деление на ноль.
   */
  readonly minMax: number;
}

/**
 * Столбиковый тренд метрики. Без библиотек и без внешних запросов: это дюжина прямоугольников,
 * а всякая графическая зависимость здесь стоила бы больше, чем рисует.
 *
 * `aria-label` перечисляет ряд словами — для скринридера график иначе пустая картинка.
 * Нулевая неделя рисуется полоской в два пикселя: столбец нулевой высоты неотличим от
 * недели, которой в отчёте нет, а это разные вещи.
 *
 * ХОЛСТ ОДИН — РАЗВЁРНУТЫЙ. Раньше у компонента было два размера, потому что тот же график
 * стоял ещё и на странице полноширинной карточкой; карточки удалены (дублировали окно), и
 * второй размер вместе с ними: единственный вызывающий — плавающее окно шириной 1180px.
 */
function TrendChart({
  label,
  points,
  format,
  max,
}: {
  readonly label: string;
  readonly points: readonly MetricPoint[];
  readonly format: (v: number) => string;
  readonly max: number;
}): JSX.Element {
  /*
   * Ширина холста ФИКСИРОВАНА, а по числу недель делится шаг. Считай мы ширину от числа
   * столбцов, две недели дали бы холст в сто пикселей, растянутый по окну впятеро вместе с
   * подписями: 11-пиксельная цифра поехала бы на шестидесяти. Здесь наоборот — при двух
   * неделях столбцы становятся шире, а текст остаётся текстом.
   */
  const width = 1120;
  const slot = width / Math.max(1, points.length);
  const bar = Math.min(40, slot * 0.6);
  const top = 26;
  const plot = 200;
  const base = top + plot;
  const height = base + 26;
  const scale = max <= 0 ? 0 : plot / max;
  /*
   * Подписи прореживаются, когда столбцов больше, чем помещается надписей: при 52 неделях
   * значение над каждым столбцом слилось бы в серую полосу. Прореживание — по индексу, и
   * последняя неделя показывается ВСЕГДА: она и есть ответ на вопрос «как сейчас».
   */
  const every = Math.max(1, Math.ceil(points.length / 26));
  const shows = (i: number): boolean => i === points.length - 1 || i % every === 0;

  return (
    <svg
      className={styles['chart']}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      role="img"
      aria-label={`${label} по неделям: ${points
        .map((p) => `${weekShort(p.week)} — ${p.value === null ? 'нет данных' : format(p.value)}`)
        .join(', ')}`}
    >
      <line
        x1={0}
        y1={base}
        x2={width}
        y2={base}
        stroke="var(--border)"
        strokeWidth={1}
        shapeRendering="crispEdges"
      />
      {points.map((p, i) => {
        const cx = i * slot + slot / 2;
        const h = p.value === null ? 0 : Math.max(2, Math.round(p.value * scale));
        return (
          <g key={p.week}>
            {h === 0 ? null : (
              <rect
                x={cx - bar / 2}
                y={base - h}
                width={bar}
                height={h}
                rx={3}
                fill="var(--violet)"
                opacity={i === points.length - 1 ? 1 : 0.72}
              />
            )}
            {shows(i) ? (
              <text
                x={cx}
                y={base - h - 8}
                textAnchor="middle"
                fontSize={11}
                fill="var(--ink-2)"
                fontFamily="var(--sans)"
              >
                {p.value === null ? '—' : format(p.value)}
              </text>
            ) : null}
            {shows(i) ? (
              <text
                x={cx}
                y={base + 17}
                textAnchor="middle"
                fontSize={10}
                fill="var(--muted)"
                fontFamily="var(--sans)"
              >
                {weekShort(p.week)}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Диапазоны развёрнутого графика. `null` — «всё, что есть»: отчёт начался в конкретный
 * день, и обещать год данных, которых нет, незачем.
 */
const RANGES: readonly {
  readonly id: string;
  readonly label: string;
  readonly weeks: number | null;
}[] = [
  { id: '12', label: '12 недель', weeks: 12 },
  { id: '26', label: 'полгода', weeks: 26 },
  { id: '52', label: 'год', weeks: 52 },
  { id: 'all', label: 'всё время', weeks: null },
];

/**
 * Плавающее окно метрики: как она считается, развёрнутый тренд и те же числа таблицей.
 *
 * Диапазон живёт ЗДЕСЬ, а не на странице: он относится к рассматриванию графика, а не к
 * состоянию экрана, и после закрытия окна помнить его незачем. Кнопки диапазона, которым
 * нечего показать (год на трёхнедельном отчёте), не прячутся, а гасятся: спрятанная кнопка
 * читается как «такой возможности нет», а её просто пока не на чем применить.
 */
function MetricDetail({
  metric,
  onClose,
}: {
  readonly metric: ChannelMetric;
  readonly onClose: () => void;
}): JSX.Element {
  const [rangeId, setRangeId] = useState<string>('12');
  const range = RANGES.find((r) => r.id === rangeId) ?? RANGES[0];
  const weeks = range?.weeks ?? null;
  const shown = weeks === null ? metric.points : metric.points.slice(-weeks);
  const max = Math.max(metric.minMax, ...shown.map((p) => p.value ?? 0));

  return (
    <Modal title={metric.label} subtitle="по неделям" width={1180} onClose={onClose}>
      <div className="micro">{metric.explain}</div>

      {/* fieldset, а не div с role="group": группа переключателей — ровно то, для чего
          элемент существует, и подпись ей даёт legend, а не aria-label поверх пустого div. */}
      <fieldset className={styles['ranges']}>
        <legend className={styles['rangesLegend']}>Диапазон графика</legend>
        {RANGES.map((r) => (
          <button
            key={r.id}
            type="button"
            className={styles['rangeButton']}
            aria-pressed={r.id === rangeId}
            disabled={r.weeks !== null && metric.points.length <= r.weeks && r.id !== rangeId}
            onClick={() => {
              setRangeId(r.id);
            }}
          >
            {r.label}
          </button>
        ))}
      </fieldset>

      <TrendChart label={metric.label} points={shown} format={metric.format} max={max} />

      <div className="meta">
        {metric.total}
        {metric.points.length === 1 ? ' · одна неделя в отчёте — тренда пока нет' : ''}
        {` · на графике недель: ${String(shown.length)} из ${String(metric.points.length)}`}
      </div>

      <div className="tblwrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Неделя с</th>
              <th>{metric.label}</th>
              <th>Из чего</th>
            </tr>
          </thead>
          <tbody>
            {[...shown].reverse().map((p) => (
              <tr key={p.week}>
                <td className={styles['numCell']}>{p.week}</td>
                <td className={styles['numCell']}>
                  {p.value === null ? '—' : metric.format(p.value)}
                </td>
                <td className={styles['numCell']}>{p.caption}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

/**
 * Русские имена метрик, у которых источника нет. Порядок карточек задаётся ЗДЕСЬ, а не
 * порядком строк вьюхи: та сортирует по латинскому коду, и на экране получилось бы
 * «реакции, подписчики, экономия, просмотры» — алфавит чужого языка.
 */
const UNAVAILABLE_LABEL: ReadonlyMap<string, string> = new Map<string, string>([
  ['subscribers', 'Подписчики'],
  ['reactions', 'Реакции'],
  ['comments', 'Комментарии'],
]);

/**
 * Причина — кодом из вьюхи; фраза живёт здесь, как и все остальные подписи экрана.
 *
 * Кодов стало два вместо четырёх (0012), и оба ВРЕМЕННЫЕ по своей природе: первый снимается
 * первым же замером сборщика, второй — появлением группы обсуждения. Прежние
 * `bot_api_has_no_method` и `no_manual_baseline` описывали метрики, которых на экране
 * больше нет вовсе: обещать прочерком источник, которого не будет, — та же неправда, что
 * показать ноль.
 */
const UNAVAILABLE_REASON: ReadonlyMap<string, string> = new Map<string, string>([
  ['collector_has_no_sample_yet', 'сбор включён, первого замера ещё не было'],
  ['needs_discussion_group', 'у канала нет группы обсуждения — комментировать негде'],
]);

/* ══ живость источников: порядок, легенда исходов, подробность ═══════════════ */

/**
 * Порядок ячеек сетки: проблемы вперёд. Порядок вьюхи (по коду) хоронит мёртвый источник
 * между здоровыми, а сетка на двадцать одну ячейку читается сверху вниз — первым должно
 * стоять то, что чинят.
 */
const SOURCE_RANK: ReadonlyMap<string, number> = new Map<string, number>([
  ['dead', 0],
  ['degraded', 1],
  ['unknown', 2],
  ['ok', 3],
]);

/** Незнакомое состояние встаёт между «неизвестно» и «работает»: прятать его нельзя. */
const rankOf = (state: string): number => SOURCE_RANK.get(state) ?? 2.5;

interface OutcomeView {
  /** Что произошло — по-русски, одной фразой. */
  readonly phrase: string;
  /** Что с этим делает оператор. Пустая строка — делать нечего, это норма. */
  readonly action: string;
}

/**
 * Легенда исходов выборки. Ключи — словарь `SOURCE_FETCH_OUTCOMES` из контрактов (он же CHECK
 * на `source_fetch.outcome`), поэтому список кодов здесь не дублируется: он импортируется, и
 * одиннадцатый исход появится в легенде сам, с честной пометкой «фраза не описана».
 *
 * Столбец «что делает оператор» — половина смысла легенды: `redirected` и `parse_error`
 * чинятся правкой реестра и селектора, а `http_error` 403 — это решение чужой стороны, и
 * знать разницу нужно до того, как начнёшь чинить (спецификация правок 08.09, §4.6).
 */
const OUTCOME_LEGEND: ReadonlyMap<string, OutcomeView> = new Map<string, OutcomeView>([
  ['ok', { phrase: 'ответ получен, парсер вернул элементы', action: '' }],
  [
    'empty_at_200',
    {
      phrase: 'страница отдалась, но контейнер доказанно пуст',
      action: 'проверить адрес в реестре: пустой раздел и неверный адрес выглядят одинаково',
    },
  ],
  [
    'http_error',
    {
      phrase: 'ответ не 2xx/3xx; 403 и 429 — нас придерживают',
      action: 'ручки вежливости наши, решение чужой стороны — нет',
    },
  ],
  [
    'network_error',
    {
      phrase: 'DNS, TCP или TLS не дошли',
      action: 'наш случай — только url_rejected (егресс); ENOTFOUND чинится не у нас',
    },
  ],
  [
    'parse_error',
    {
      phrase: 'разметка уехала из-под селектора или это наш баг',
      action: 'чиним кодом: класс «неверный селектор»',
    },
  ],
  [
    'stub_detected',
    {
      phrase: 'вместо материала пришла заглушка или антибот',
      action: 'чаще всего устаревший адрес в реестре',
    },
  ],
  ['timeout', { phrase: 'ответа не было за timeout_ms', action: 'поднять timeout_ms источнику' }],
  ['too_large', { phrase: 'тело больше max_bytes', action: 'поднять max_bytes источнику' }],
  [
    'redirected',
    {
      phrase: 'источник переехал; адрес назначения — в тексте ошибки',
      action: 'вписать новый адрес в реестр: это самая частая настоящая причина смерти',
    },
  ],
  [
    'not_this_parser',
    {
      phrase: 'парсер отказался от страницы или не подключён',
      action: 'чиним кодом: разобрать, почему цепочка не подхватила',
    },
  ],
]);

/**
 * Момент попытки. Поле нормализованное (`started_at`), но регистрация вьюхи в белом списке
 * бэкенда приезжает отдельной правкой — пока в её `instants` нет колонки, момент придёт
 * числом. Тогда показываем прочерк: миллисекунды на экране оператора — это не время.
 */
const fetchStamp = (r: SourceFetchRow): string =>
  typeof r.started_at === 'string' ? shortMsk(r.started_at) : '—';

/** `job_run.kind` — CHECK таблицы; список закрыт схемой. */
const RUN_KIND_LABEL: ReadonlyMap<string, string> = new Map<string, string>([
  ['monitor', 'обход источников'],
  ['generate', 'генерация'],
  ['publish_tick', 'тик публикатора'],
  ['maintenance', 'обслуживание'],
  ['fixture_load', 'загрузка фикстур'],
]);

/** `monitoring_run.state`, а у прогонов без мониторинга — `job_run.status` (COALESCE вьюхи). */
const RUN_STATE: ReadonlyMap<string, StateView> = new Map<string, StateView>([
  ['running', { label: 'идёт', dot: '', color: 'var(--ink-2)' }],
  ['ok', { label: 'успешно', dot: 'dot-ok', color: 'var(--ink-2)' }],
  ['partial', { label: 'частично', dot: 'dot-warn', color: 'var(--amber-ink)' }],
  ['failed', { label: 'сорван', dot: 'dot-err', color: 'var(--red-ink)' }],
  ['overlapped', { label: 'наложился', dot: 'dot-warn', color: 'var(--amber-ink)' }],
]);

/** Строка списка оценок: подпись версии и две кнопки. Пишет единственный маршрут записи экрана. */
function MarkRow({
  row,
  busy,
  onMark,
}: {
  readonly row: QualityMarkRow;
  readonly busy: boolean;
  readonly onMark: (row: QualityMarkRow, mark: 'like' | 'dislike') => void;
}): JSX.Element {
  return (
    <tr>
      <td>
        <div className="small" style={{ color: 'var(--ink)' }}>
          {row.excerpt}
        </div>
        <div className="meta">
          {row.format_code === null ? 'формат не указан' : formatLabel(row.format_code)} · версия{' '}
          {fmtInt(row.version_no)} · {orDash(row.created_at)}
        </div>
      </td>
      <td className={styles['codeCell']}>{row.status}</td>
      <td className={styles['numCell']}>
        {fmtInt(row.likes)} / {fmtInt(row.dislikes)}
      </td>
      <td className={styles['numCell']}>{orDash(row.marked_at)}</td>
      <td>
        <div className={styles['markButtons']}>
          <button
            type="button"
            className={busy ? 'btn btn-sm btn-dis' : 'btn btn-sm'}
            disabled={busy}
            onClick={() => {
              onMark(row, 'like');
            }}
          >
            Нравится
          </button>
          <button
            type="button"
            className={busy ? 'btn btn-sm btn-dis' : 'btn btn-sm'}
            disabled={busy}
            onClick={() => {
              onMark(row, 'dislike');
            }}
          >
            Не нравится
          </button>
        </div>
      </td>
    </tr>
  );
}

export function MonitoringPage({ brand }: { brand: string }): JSX.Element {
  const publish = useResource<PublishDailyRow[]>('/api/v1/ops/publish-daily');
  const watchdog = useResource<WatchdogRow[]>('/api/v1/ops/watchdog');
  const schedule = useResource<ScheduleRow[]>('/api/v1/ops/schedule');
  const httpErrors = useResource<HttpErrorRow[]>('/api/v1/ops/http-errors-daily');
  // Источники — не вьюха, а маршрут со схемой (см. шапку): вес и включённость в трёх слоях.
  const srcPath = `/api/v1/sources?brand=${encodeURIComponent(brand)}`;
  const sources = useResource<unknown>(srcPath);
  const delivery = useResource<AlertDeliveryRow[]>('/api/v1/ops/alert-delivery-daily');
  const llm = useResource<LlmDailyRow[]>('/api/v1/ops/llm-daily');
  const cache = useResource<LlmCacheRow[]>('/api/v1/ops/llm-cache-daily');
  const prefilter = useResource<PrefilterRow[]>('/api/v1/ops/prefilter-daily');
  const deepCost = useResource<DeepCostRow[]>('/api/v1/ops/deep-cost-daily');
  const quality = useResource<QualityDailyRow[]>('/api/v1/ops/quality-daily');
  const visual = useResource<VisualShareRow[]>('/api/v1/ops/visual-share');
  const marks = useResource<QualityMarkRow[]>('/api/v1/ops/quality-marks');
  const weekly = useResource<PublishWeeklyRow[]>('/api/v1/ops/publish-weekly');
  const channel = useResource<ChannelMetricsWeeklyRow[]>('/api/v1/ops/channel-metrics-weekly');
  const unavailable = useResource<MetricUnavailableRow[]>('/api/v1/ops/metrics-unavailable');
  /*
   * Прогоны и журнал попыток. Вьюхи и потолки у обеих запечены внутрь (0004 и 0017), поэтому
   * запрос без параметров — это уже ограниченный запрос.
   *
   * `source-fetch-recent` регистрируется в белом списке бэкенда ОТДЕЛЬНОЙ правкой (сама вьюха
   * пришла миграцией 0017). Пока её нет, маршрут отвечает 404, и это не авария экрана:
   * подробность источника показывает свою причину тем же `DataState`, а сетка живости,
   * здоровье и текст последней ошибки работают без неё.
   */
  const runs = useResource<RunRow[]>('/api/v1/ops/runs');
  const fetches = useResource<SourceFetchRow[]>('/api/v1/ops/source-fetch-recent');

  const [markBusy, setMarkBusy] = useState<string | null>(null);
  const [markError, setMarkError] = useState<string | null>(null);
  const [openMetric, setOpenMetric] = useState<string | null>(null);
  /** Источник, чья панель открыта: подробность и ручки — для ОДНОГО источника. */
  const [openSource, setOpenSource] = useState<string | null>(null);
  // Стабильная функция закрытия: `Modal` держит фокус эффектом, зависящим от неё
  // (docs/frontend.md §«Прогон мониторинга»), и новая на каждой перерисовке дёргала бы фокус.
  const closeSource = useCallback((): void => {
    setOpenSource(null);
  }, []);
  const refetchSources = sources.refetch;
  const onSourceSaved = useCallback((): void => {
    refetchSources();
  }, [refetchSources]);
  /** Какой разрез расхода LLM нарисован. Таблица рисуется ровно одна — см. шапку файла. */
  const [llmCut, setLlmCut] = useState<'stage' | 'cache'>('stage');

  /*
   * Сводка алертов приезжает из `AlertsList` (он владеет и выборкой, и подтверждением).
   * Здесь она нужна ровно для двух вещей: бейдж в заголовке, видимый в свёрнутом виде, и
   * умолчание «раскрыт ли раздел».
   */
  const [alertSummary, setAlertSummary] = useState<AlertsSummary>({
    state: 'loading',
    total: 0,
    critical: 0,
    error: 0,
    warning: 0,
  });
  /**
   * Умолчание «Требует внимания» считается ОДИН РАЗ, на первой удачной загрузке, и дальше не
   * пересчитывается. Иначе подтверждение последнего критичного алерта схлопнуло бы раздел
   * под курсором — ровно в тот момент, когда человек читает соседнюю строку.
   */
  const [attentionDefault, setAttentionDefault] = useState<boolean | null>(null);

  const onAlertSummary = useCallback((s: AlertsSummary): void => {
    setAlertSummary((prev) =>
      prev.state === s.state &&
      prev.total === s.total &&
      prev.critical === s.critical &&
      prev.error === s.error &&
      prev.warning === s.warning
        ? prev
        : s,
    );
    setAttentionDefault((prev) =>
      prev !== null || s.state !== 'ready' ? prev : s.critical + s.error > 0,
    );
  }, []);

  /*
   * Строки представлений отбираются по бренду экрана там, где у вьюхи есть `brand_code`.
   * Строки без бренда (ожидания watchdog всего контура) остаются: они относятся ко всему
   * контуру, и спрятать их на вкладке бренда значило бы спрятать сломанный публикатор.
   */
  const ofBrand = <T extends { readonly brand_code: string | null }>(rows: readonly T[]): T[] =>
    rows.filter((r) => r.brand_code === brand || r.brand_code === null);

  const pubRows = ofBrand(publish.data ?? []);
  const wdRows = ofBrand(watchdog.data ?? []);
  // Обещания — строго своего бренда: у вьюхи бренд есть всегда.
  const schedRows = (schedule.data ?? []).filter((r) => r.brand_code === brand);
  const schedFailed = schedRows.filter((r) => SCHEDULE_FAILED.has(r.state));
  const httpRows = httpErrors.data ?? [];
  const http5xx = httpRows.filter((r) => r.status >= 500).reduce((n, r) => n + r.count, 0);
  /*
   * Ответ источников разбирается СХЕМОЙ: чужой ответ (старый бэкенд без маршрута, HTML
   * прокси) — строка «не загрузилось» в своём разделе, а не исключение посреди экрана.
   */
  const srcFresh = useMemo((): readonly SourceControl[] | null => {
    if (sources.data === undefined) return null;
    const parsed = SourceControlsResponseSchema.safeParse(sources.data);
    return parsed.success ? parsed.data.sources : null;
  }, [sources.data]);
  /*
   * Последний удачный список (`useLastGood`): список перечитывается после каждого сохранения в
   * панели источника, и сбой этого GET не имеет права закрыть открытую панель и стереть сетку —
   * человек только что сохранил правку и смотрит на её результат. Сбой показывается строкой
   * «не обновилось» над прежним списком.
   */
  const srcGood = useLastGood(srcPath, sources, srcFresh);
  const srcRows: readonly SourceControl[] = srcGood.value ?? [];
  const srcError =
    srcGood.failure === null
      ? undefined
      : new Error(
          srcGood.value === null
            ? srcGood.failure
            : `список не обновился, показан прежний — ${srcGood.failure}`,
        );
  const delRows = delivery.data ?? [];
  const llmRows = ofBrand(llm.data ?? []);
  const cacheRows = ofBrand(cache.data ?? []);
  const preRows = ofBrand(prefilter.data ?? []);
  const deepRows = ofBrand(deepCost.data ?? []);
  const qualRows = ofBrand(quality.data ?? []);
  const markRows = ofBrand(marks.data ?? []);
  // Колонка бренда у этой вьюхи называется `brand`, поэтому `ofBrand` (он смотрит на
  // `brand_code`) здесь неприменим — фильтр повторён явно, а не подогнан переименованием.
  const visualRows = (visual.data ?? []).filter((r) => r.brand === brand || r.brand === null);
  const unavailRows = ofBrand(unavailable.data ?? []);
  const runRows = ofBrand(runs.data ?? []);
  /*
   * Журнал попыток по бренду не фильтруется: у `source_fetch` бренда нет вовсе — источник
   * принадлежит реестру, а не бренду. Фильтр здесь один и клиентский — по выбранной ячейке.
   */
  const fetchRows = fetches.data ?? [];

  /*
   * Недели идут по возрастанию — вьюха отдаёт их сверху вниз (свежая первой), а график
   * читается слева направо. Строки без недели отбрасываются: поставить их на ось нечем, а
   * подмешивать в суммы то, чего нет на графике, значит показать два разных ответа.
   */
  const wkRows = ofBrand(weekly.data ?? [])
    .filter((r): r is PublishWeeklyRow & { week_start: string } => r.week_start !== null)
    .sort((a, b) => a.week_start.localeCompare(b.week_start));
  const wkLast = wkRows.at(-1);
  const wkPrev = wkRows.at(-2);
  const publishedAll = sumOf(wkRows, (r) => r.published).sum;
  const noReworkAll = sumOf(wkRows, (r) => r.no_rework).sum;
  const handsOffAll = sumOf(wkRows, (r) => r.hands_off).sum;
  const weekNote = wkLast === undefined ? '' : `неделя с ${weekShort(wkLast.week_start)}`;

  /*
   * Значение карточки — ПОСЛЕДНЯЯ НЕДЕЛЯ, а не среднее за весь срок: «стрелочка к прошлой
   * неделе» имеет смысл только рядом с недельным числом, а итог за всё время показан в окне
   * подробностей. Доля на неделе без публикаций приходит `null` и рисуется прочерком —
   * ноль означал бы «ни один пост не прошёл без доработки», а постов не было вовсе.
   */
  const channelMetrics: readonly ChannelMetric[] =
    wkLast === undefined
      ? []
      : [
          {
            id: 'published',
            label: 'Постов за неделю',
            value: fmtInt(wkLast.published),
            note: `${weekNote} · ${weekDelta(wkLast.published, wkPrev?.published ?? null, 'posts').text}`,
            tone: weekDelta(wkLast.published, wkPrev?.published ?? null, 'posts').tone,
            explain:
              'Считаются посты, дошедшие до канала: работа публикации с номером сообщения от Telegram. ' +
              'Отозванный пост из счёта не вычитается — он был опубликован и его видели.',
            total: `за весь срок: ${fmtInt(publishedAll)} постов за ${fmtInt(wkRows.length)} нед.`,
            points: wkRows.map((r) => ({
              week: r.week_start,
              value: r.published,
              caption: `отозвано ${fmtInt(r.retracted)}`,
            })),
            format: fmtInt,
            minMax: 1,
          },
          {
            id: 'no-rework',
            label: 'Без доработки',
            value: fmtPct(wkLast.no_rework_ratio),
            note: `${weekNote} · ${weekDelta(wkLast.no_rework_ratio, wkPrev?.no_rework_ratio ?? null, 'pp').text}`,
            tone: weekDelta(wkLast.no_rework_ratio, wkPrev?.no_rework_ratio ?? null, 'pp').tone,
            explain:
              'Доля постов, которые ни разу не отправляли на переделку. Одобрение человеком доработкой ' +
              'не считается: без него пост в канал не попадает вовсе (FR-4.1).',
            total: `за весь срок: ${fmtInt(noReworkAll)} из ${fmtInt(publishedAll)}`,
            points: wkRows.map((r) => ({
              week: r.week_start,
              value: r.no_rework_ratio,
              caption: `${fmtInt(r.no_rework)} из ${fmtInt(r.published)}`,
            })),
            format: fmtPct0,
            minMax: 1,
          },
          {
            id: 'hands-off',
            label: 'Без правки руками',
            value: fmtPct(wkLast.hands_off_ratio),
            note: `${weekNote} · ${weekDelta(wkLast.hands_off_ratio, wkPrev?.hands_off_ratio ?? null, 'pp').text}`,
            tone: weekDelta(wkLast.hands_off_ratio, wkPrev?.hands_off_ratio ?? null, 'pp').tone,
            explain:
              'Доля постов, текст которых человек не трогал: ни переделки, ни правки в редакторе. ' +
              'Это и есть «регулярность без ручного вмешательства в контент» из §10 ТЗ.',
            total: `за весь срок: ${fmtInt(handsOffAll)} из ${fmtInt(publishedAll)}`,
            points: wkRows.map((r) => ({
              week: r.week_start,
              value: r.hands_off_ratio,
              caption: `${fmtInt(r.hands_off)} из ${fmtInt(r.published)}`,
            })),
            format: fmtPct0,
            minMax: 1,
          },
        ];

  /*
   * ── Метрики канала из наблюдений (0012) ───────────────────────────────────
   *
   * Отдельный ряд от `channelMetrics`, потому что источник другой и полнота другая:
   * недели публикаций есть всегда, а недели замеров начинаются с того дня, как включили
   * сбор. Склеивать их в один список значило бы показать дыры там, где их нет.
   */
  const cmRows = ofBrand(channel.data ?? [])
    .filter((r): r is ChannelMetricsWeeklyRow & { week_start: string } => r.week_start !== null)
    .sort((a, b) => a.week_start.localeCompare(b.week_start));
  const cmLast = cmRows.at(-1);
  const cmPrev = cmRows.at(-2);
  const cmNote = cmLast === undefined ? '' : `неделя с ${weekShort(cmLast.week_start)}`;

  /** Метрика показывается, только если по ней есть хоть одно наблюдение за всё время. */
  const observed = (pick: (r: ChannelMetricsWeeklyRow) => number | null): boolean =>
    cmRows.some((r) => pick(r) !== null);

  const subscribersAll = cmRows.filter((r) => r.subscribers !== null).at(-1)?.subscribers ?? null;
  const reactionsAll = sumOf(cmRows, (r) => r.reactions);
  const commentsAll = sumOf(cmRows, (r) => r.comments);

  const channelSeries: readonly ChannelMetric[] =
    cmLast === undefined
      ? []
      : [
          ...(observed((r) => r.subscribers)
            ? [
                {
                  id: 'subscribers',
                  label: 'Подписчики',
                  value: cmLast.subscribers === null ? null : fmtInt(cmLast.subscribers),
                  note: `${cmNote} · ${weekDelta(cmLast.subscribers, cmPrev?.subscribers ?? null, 'posts').text}`,
                  tone: weekDelta(cmLast.subscribers, cmPrev?.subscribers ?? null, 'posts').tone,
                  explain:
                    'Число участников канала по getChatMemberCount. Значение недели — ПОСЛЕДНИЙ ' +
                    'замер внутри неё: подписчики это запас, а не поток, и складывать замеры нельзя. ' +
                    'Стрелка показывает прирост к прошлой неделе.',
                  total:
                    subscribersAll === null
                      ? 'замеров ещё не было'
                      : `последний замер: ${fmtInt(subscribersAll)}`,
                  points: cmRows.map((r) => ({
                    week: r.week_start,
                    value: r.subscribers,
                    caption: r.subscribers === null ? 'не замеряли' : 'на конец недели',
                  })),
                  format: fmtInt,
                  minMax: 1,
                } satisfies ChannelMetric,
              ]
            : []),
          ...(observed((r) => r.reactions)
            ? [
                {
                  id: 'reactions',
                  label: 'Реакции',
                  value: cmLast.reactions === null ? null : fmtInt(cmLast.reactions),
                  note: `${cmNote} · ${weekDelta(cmLast.reactions, cmPrev?.reactions ?? null, 'posts').text}`,
                  tone: weekDelta(cmLast.reactions, cmPrev?.reactions ?? null, 'posts').tone,
                  explain:
                    'Сумма реакций на постах, опубликованных В ЭТУ неделю, — по апдейтам ' +
                    'message_reaction_count от Telegram. Реакция, поставленная позже, идёт в неделю ' +
                    'ПОСТА, а не в неделю, когда её увидели. Telegram шлёт эти апдейты пачками с ' +
                    'задержкой до нескольких минут, поэтому счётчик отстаёт от канала на минуты.',
                  total: `за весь срок: ${fmtInt(reactionsAll.sum)} на ${fmtInt(
                    cmRows.reduce((n, r) => n + r.reaction_posts, 0),
                  )} постах`,
                  points: cmRows.map((r) => ({
                    week: r.week_start,
                    value: r.reactions,
                    caption:
                      r.reactions === null
                        ? 'не наблюдали'
                        : `на ${fmtInt(r.reaction_posts)} из ${fmtInt(r.posts)} постов`,
                  })),
                  format: fmtInt,
                  minMax: 1,
                } satisfies ChannelMetric,
              ]
            : []),
          ...(observed((r) => r.comments)
            ? [
                {
                  id: 'comments',
                  label: 'Комментарии',
                  value: cmLast.comments === null ? null : fmtInt(cmLast.comments),
                  note: `${cmNote} · ${weekDelta(cmLast.comments, cmPrev?.comments ?? null, 'posts').text}`,
                  tone: weekDelta(cmLast.comments, cmPrev?.comments ?? null, 'posts').tone,
                  explain:
                    'Ответы в тредах группы обсуждения, отнесённые к посту канала по пересланному ' +
                    'сообщению. Считаются с того дня, как у канала появилась группа обсуждения; ' +
                    'удаление комментария Telegram ботам не показывает, поэтому счётчик не убывает.',
                  total: `за весь срок: ${fmtInt(commentsAll.sum)} на ${fmtInt(
                    cmRows.reduce((n, r) => n + r.comment_posts, 0),
                  )} постах`,
                  points: cmRows.map((r) => ({
                    week: r.week_start,
                    value: r.comments,
                    caption:
                      r.comments === null
                        ? 'не наблюдали'
                        : `на ${fmtInt(r.comment_posts)} из ${fmtInt(r.posts)} постов`,
                  })),
                  format: fmtInt,
                  minMax: 1,
                } satisfies ChannelMetric,
              ]
            : []),
        ];

  const allMetrics = [...channelMetrics, ...channelSeries];
  const shownMetric = allMetrics.find((m) => m.id === openMetric);

  // Порядок карточек — наш, а не алфавит латинских кодов вьюхи; незнакомый код не прячется.
  const unavailableCards = [...unavailRows].sort((a, b) => {
    const order = [...UNAVAILABLE_LABEL.keys()];
    const ia = order.indexOf(a.metric);
    const ib = order.indexOf(b.metric);
    return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib);
  });

  const overdue = wdRows.filter((r) => r.state === 'overdue');

  /*
   * ── «Требует внимания»: бейдж и умолчание ────────────────────────────────
   *
   * Бейдж собирается из счётчиков и виден ВСЕГДА, в том числе у свёрнутого раздела: свёрнутое
   * прячет подробности, но не факт проблемы. Ноль в бейдж не попадает — «0 предупреждений»
   * это не сведение, а шум.
   */
  const attentionParts = [
    alertSummary.critical > 0 ? `${fmtInt(alertSummary.critical)} критично` : '',
    alertSummary.error > 0
      ? `${fmtInt(alertSummary.error)} ${plural(alertSummary.error, 'ошибка', 'ошибки', 'ошибок')}`
      : '',
    alertSummary.warning > 0
      ? `${fmtInt(alertSummary.warning)} ${plural(alertSummary.warning, 'предупреждение', 'предупреждения', 'предупреждений')}`
      : '',
    overdue.length > 0 ? `${fmtInt(overdue.length)} просрочено` : '',
  ].filter((p) => p !== '');
  const attentionCount = alertSummary.total + overdue.length;
  /*
   * Ровная строка — пока действовать не по чему. Отказ выборки алертов в неё НЕ сворачивается:
   * «открытых алертов нет» при неотвеченном запросе — то самое утверждение, ради ложности
   * которого экран и построен, поэтому там раздел с телом и красной плашкой внутри.
   *
   * Пока список грузится, строка тоже ровная, но говорит «проверяем»: заголовок-кнопка,
   * который через полсекунды схлопывается в строку, — это мигание на первом экране.
   */
  const attentionCalm = alertSummary.state !== 'error' && attentionCount === 0;
  const attentionCalmNote =
    alertSummary.state === 'loading' ? '— проверяем открытые алерты…' : '— открытых алертов нет';
  const attentionBadge =
    attentionParts.length > 0
      ? attentionParts.join(' · ')
      : alertSummary.state === 'error'
        ? 'список алертов не загрузился'
        : null;

  /*
   * ── Живость источников: порядок, бейдж, выбранная ячейка ──────────────────
   */
  // Включённые — вперёд и по тяжести, выключенные — в конец: мёртвый, но выключенный источник
  // чинить не нужно, и стоять среди аварий ему незачем. Он же не входит в бейдж раздела.
  const srcSorted = [...srcRows].sort(
    (a, b) =>
      Number(b.enabled) - Number(a.enabled) ||
      rankOf(cellStateOf(a).state) - rankOf(cellStateOf(b).state) ||
      a.source_code.localeCompare(b.source_code),
  );
  const srcLive = srcRows.filter((r) => r.enabled);
  const deadCount = srcLive.filter((r) => r.health.state === 'dead').length;
  const degradedCount = srcLive.filter((r) => r.health.state === 'degraded').length;
  const noYieldCount = srcLive.filter((r) => cellStateOf(r).byYield).length;
  const srcBadge =
    deadCount + degradedCount + noYieldCount === 0
      ? null
      : [
          deadCount > 0
            ? `${fmtInt(deadCount)} ${plural(deadCount, 'мёртв', 'мёртвых', 'мёртвых')}`
            : '',
          degradedCount > 0
            ? `${fmtInt(degradedCount)} ${plural(degradedCount, 'деградирует', 'деградируют', 'деградируют')}`
            : '',
          noYieldCount > 0 ? `${fmtInt(noYieldCount)} без выхода` : '',
        ]
          .filter((p) => p !== '')
          .join(' · ');
  const shownSource = srcSorted.find((r) => r.source_code === openSource);
  const sourceFetches =
    shownSource === undefined
      ? []
      : fetchRows.filter((r) => r.source_code === shownSource.source_code);
  /*
   * «Фид жив, а статьи не выкачиваются» — единственный сигнал об этом на всём экране.
   * Попытки с `purpose='article'` СОЗНАТЕЛЬНО исключены из окна здоровья источника (шестьдесят
   * успешных статей не должны маскировать мёртвый фид), поэтому здоровье о них молчит по
   * правилу, а не по недосмотру. Обратной сигнализации не было вовсе — вот она.
   */
  const articleRows = sourceFetches.filter((r) => r.purpose === 'article');
  const articleOk = articleRows.filter((r) => r.outcome === 'ok').length;

  /* ── Прогоны мониторинга: бейдж по сорванным ── */
  const runsBad = runRows.filter((r) => r.state === 'failed' || r.state === 'partial').length;

  const hasPub = pubRows.length > 0;
  const totals = pubRows.reduce(
    (acc, r) => ({
      attempts: acc.attempts + r.attempts,
      sent: acc.sent + r.sent,
      failed: acc.failed + r.failed,
      deviations: acc.deviations + r.deviations,
    }),
    { attempts: 0, sent: 0, failed: 0, deviations: 0 },
  );

  const pubNote = hasPub ? `дней в отчёте: ${fmtInt(pubRows.length)}` : 'данных пока нет';

  /*
   * Сводные доли пересчитываются ИЗ СУММ, а не усредняются по дням: среднее долей взвешивает
   * сутки с тремя материалами так же, как сутки с тремя сотнями, и на такой цифре вырождение
   * префильтра не видно.
   */
  const seenTotal = sumOf(preRows, (r) => r.materials_seen).sum;
  const heurTotal = sumOf(preRows, (r) => r.passed_heuristic).sum;
  const llmPassedTotal = sumOf(preRows, (r) => r.passed_llm).sum;
  const topicsTotal = sumOf(preRows, (r) => r.topics_created).sum;
  const dropRatio = heurTotal === 0 ? null : (heurTotal - llmPassedTotal) / heurTotal;
  const dropNote = ratioNote(
    dropRatio,
    'прогонов мониторинга ещё не было',
    'отсев 0%: рубрика пропускает всё дальше',
    'отсев 100%: рубрика не пропускает ничего',
    `материалов до префильтра: ${fmtInt(heurTotal)}`,
  );

  const promptTotal = sumOf(cacheRows, (r) => r.prompt_tokens).sum;
  const readTotal = sumOf(cacheRows, (r) => r.cache_read_tokens).sum;
  const callsTotal = sumOf(cacheRows, (r) => r.calls).sum;
  const cacheRatio = promptTotal === 0 ? null : readTotal / promptTotal;
  const cacheNote = ratioNote(
    cacheRatio,
    'вызовов модели ещё не было',
    'кэш не читается: счёт вырастет кратно',
    'весь промпт из кэша',
    `вызовов: ${fmtInt(callsTotal)}`,
  );

  const spendTotal = sumOf(llmRows, (r) => r.cost_total_micro_usd);
  const deepItems = sumOf(deepRows, (r) => r.deep_items);
  const deepSpend = sumOf(deepRows, (r) => r.deep_micro_usd);
  const deepPerItem =
    deepItems.has && deepSpend.has && deepItems.sum > 0 ? deepSpend.sum / deepItems.sum : null;

  const approvals = sumOf(qualRows, (r) => r.approvals).sum;
  const firstPass = sumOf(qualRows, (r) => r.first_pass).sum;
  const afterRework = sumOf(qualRows, (r) => r.after_rework).sum;
  const likes = sumOf(qualRows, (r) => r.likes).sum;
  const dislikes = sumOf(qualRows, (r) => r.dislikes).sum;
  const firstPassRatio = approvals === 0 ? null : firstPass / approvals;
  const reworkRatio = approvals === 0 ? null : afterRework / approvals;
  const qualNote = approvals === 0 ? 'одобрений ещё не было' : `одобрений: ${fmtInt(approvals)}`;

  // Доля считается ЗАНОВО из сумм, а не берётся из колонки `share`: у двух брендов долей две,
  // и складывать проценты нельзя. При одном бренде результат совпадает с колонкой.
  const visualVersions = sumOf(visualRows, (r) => r.versions).sum;
  const visualDrawn = sumOf(visualRows, (r) => r.drawn).sum;
  const visualShare = visualVersions === 0 ? null : visualDrawn / visualVersions;
  const visualNote = visualShareNote(visualShare, visualVersions);

  const sendMark = (row: QualityMarkRow, mark: 'like' | 'dislike'): void => {
    const key = `${row.draft_id}/${String(row.version_no)}`;
    if (markBusy !== null) return;
    setMarkBusy(key);
    setMarkError(null);
    post<unknown>(`/api/v1/drafts/${row.draft_id}/versions/${String(row.version_no)}/mark`, {
      mark,
    })
      .catch((e: unknown) => {
        setMarkError(e instanceof ApiRequestError || e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setMarkBusy(null);
        // Перечитываются обе половины: счётчик строки и суточный разрез — это одна отметка,
        // и показывать их по разным моментам времени нельзя.
        marks.refetch();
        quality.refetch();
      });
  };

  return (
    <Shell brand={brand} active="monitoring">
      <div className={styles['page']}>
        <div className={styles['head']}>
          <div className={styles['headText']}>
            <div className="h1">Мониторинг</div>
            <div className="meta">
              <BrandTitle id={brand} /> · цифры берутся только из операционных представлений v_ops_*
              · период не фильтруется: показан весь накопленный срок
            </div>
          </div>
        </div>

        {/* ══ требует внимания: открытые алерты и просроченные ожидания ══ */}
        {/*
          Порядок 26.08 не тронут — блок остался первым. Изменился его РАЗМЕР: когда
          действовать не по чему, это одна строка; когда есть — свёрнутый раздел со счётчиком
          в заголовке. Абзац про смысл подтверждения уехал внутрь тела: это инструкция тому,
          кто уже собрался нажать кнопку, а не факт, который надо видеть всегда.
        */}
        <Section
          id="attention"
          title="Требует внимания"
          badge={attentionBadge}
          alarm={attentionCount > 0}
          defaultOpen={attentionDefault}
          calm={attentionCalm}
          calmNote={attentionCalmNote}
        >
          <AlertsList onSummary={onAlertSummary} />

          {overdue.length === 0 ? null : (
            <div className="stack" style={{ gap: 8 }}>
              <div className={styles['sectionHead']}>
                <div className="h3">Просроченные ожидания</div>
                <div style={{ flex: 1 }} />
                <span className="meta">источник — v_ops_watchdog</span>
              </div>
              {overdue.map((r) => (
                <div key={r.key} className="vrow" style={{ gap: 12 }}>
                  <StateCell view={WATCHDOG_STATE.get(r.state) ?? UNKNOWN_STATE} />
                  <span className="small" style={{ color: 'var(--ink)' }}>
                    {r.label}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span className="meta">ждали до {orDash(r.expected_by)}</span>
                </div>
              ))}
              <div className="micro">
                Сервис не отчитался о своём цикле в срок. Полная таблица ожиданий — ниже, в
                операционном разрезе.
              </div>
            </div>
          )}
        </Section>

        {/* ══ метрики канала: §10 ТЗ ══ */}
        <Section
          id="metrics"
          title="Метрики канала"
          meta={`${allMetrics.length === 0 ? '' : 'нажмите на метрику — покажем тренд · '}источник — v_ops_publish_weekly, v_ops_channel_metrics_weekly`}
          cards={
            <>
              {/*
                Заглушки недель публикаций и карточки метрик канала показываются ВМЕСТЕ, а не
                вместо друг друга: у рядов разные источники и разная полнота. Раньше пустая
                неделя публикаций прятала и карточки замеров — график подписчиков рисовался, а
                карточки над ним не было вовсе (F-033).
              */}
              <div className={styles['kpis3']}>
                {channelMetrics.length === 0 ? (
                  <>
                    <KpiCard label="Постов за неделю" value={null} note="публикаций ещё не было" />
                    <KpiCard label="Без доработки" value={null} note="публикаций ещё не было" />
                    <KpiCard label="Без правки руками" value={null} note="публикаций ещё не было" />
                  </>
                ) : null}
                {allMetrics.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={styles['metricButton']}
                    aria-haspopup="dialog"
                    aria-label={`${m.label}: тренд по неделям`}
                    onClick={() => {
                      setOpenMetric(m.id);
                    }}
                  >
                    <KpiCard label={m.label} value={m.value} note={m.note} tone={m.tone} />
                  </button>
                ))}
              </div>

              {unavailableCards.length === 0 ? null : (
                <div className={styles['kpis']}>
                  {unavailableCards.map((r) => (
                    <KpiCard
                      key={r.metric}
                      label={UNAVAILABLE_LABEL.get(r.metric) ?? r.metric}
                      value={null}
                      note={UNAVAILABLE_REASON.get(r.reason) ?? `источника нет: ${r.reason}`}
                    />
                  ))}
                </div>
              )}
            </>
          }
          aside={
            <>
              {/* Две вьюхи — два состояния загрузки. Ошибку второй нельзя проглотить: пустой
                  блок метрик наблюдения читался бы как «сбор не даёт данных», а на деле это
                  отказ запроса. Оба состояния стоят СНАРУЖИ тела, рядом с карточками: они
                  объясняют, почему в карточках прочерк. */}
              <DataState
                loading={weekly.loading}
                error={weekly.error}
                empty={wkRows.length === 0}
                emptyText="Недель в отчёте пока нет: первая появится, когда в канал уйдёт первый пост."
              />
              <DataState
                loading={channel.loading}
                error={channel.error}
                empty={false}
                emptyText=""
              />
            </>
          }
        >
          {/* Справка, а не метрика: длинный текст под карточками и был половиной «длинного
              коридора». Он нужен один раз — когда человек спрашивает, что значит прочерк. */}
          <div className={styles['tableNote']}>
            <span className="micro">
              Прочерк значит «наблюдения ещё нет», а не ноль: ноль здесь — полноценный ответ и
              показывается числом. Подписчиков считает публикатор по getChatMemberCount, реакции
              приходят апдейтами message_reaction_count и отстают от канала на минуты. Комментарии
              начнут считаться сами в тот день, когда у канала появится группа обсуждения и бот
              войдёт в неё, — ни выкладки, ни перезапуска для этого не нужно. Просмотров на этом
              экране нет намеренно: у Bot API нет ни метода, ни апдейта, отдающего их, а чужие
              источники отклонены — показать их нечем и обещать нечего. Лайки в блоке «Качество
              согласования» — это отметки редактора, а не реакции подписчиков, и подменять ими
              вовлечённость нельзя.
            </span>
          </div>
        </Section>

        {/* ══ публикации ══ */}
        <Section
          id="publish"
          title="Публикации"
          meta="источник — v_ops_publish_daily"
          cards={
            <div className={styles['kpis']}>
              <KpiCard
                label="Попыток отправки"
                value={hasPub ? fmtInt(totals.attempts) : null}
                note={pubNote}
              />
              <KpiCard
                label="Отправлено"
                value={hasPub ? fmtInt(totals.sent) : null}
                note={pubNote}
              />
              <KpiCard
                label="Не отправлено"
                value={hasPub ? fmtInt(totals.failed) : null}
                note={pubNote}
                tone={hasPub && totals.failed > 0 ? 'down' : 'muted'}
              />
              <KpiCard
                label="Расхождений при сверке"
                value={hasPub ? fmtInt(totals.deviations) : null}
                note={hasPub ? 'отправленное ≠ канонический текст' : pubNote}
                tone={hasPub && totals.deviations > 0 ? 'down' : 'muted'}
              />
            </div>
          }
          aside={
            <DataState
              loading={publish.loading}
              error={publish.error}
              empty={!hasPub}
              emptyText="Публикаций ещё не было: первая строка появится после первой попытки отправки в канал."
            />
          }
        >
          {hasPub ? (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>День</th>
                    <th>Бренд</th>
                    <th>Попыток</th>
                    <th>Отправлено</th>
                    <th>Не отправлено</th>
                    <th>Расхождений</th>
                    <th>Средняя длительность</th>
                  </tr>
                </thead>
                <tbody>
                  {pubRows.map((r) => (
                    <tr key={`${r.day ?? '—'}/${r.brand_code ?? '—'}`}>
                      <td className={styles['numCell']}>{r.day ?? '—'}</td>
                      <td className={styles['codeCell']}>{r.brand_code ?? '—'}</td>
                      <td className={styles['numCell']}>{fmtInt(r.attempts)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.sent)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.failed)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.deviations)}</td>
                      <td className={styles['numCell']}>{fmtMs(r.avg_latency_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </Section>

        {/* ══ расписание: обещания автозаявок и их исход (0024) ══ */}
        <Section
          id="schedule"
          title="Расписание: сегодня и неделя"
          meta="источник — v_ops_schedule"
          badge={schedFailed.length === 0 ? null : `${fmtInt(schedFailed.length)} не вышло`}
          alarm={schedFailed.length > 0}
          aside={
            <DataState
              loading={schedule.loading}
              error={schedule.error}
              empty={schedRows.length === 0}
              emptyText="Обещаний расписания за неделю нет: строка появляется, когда открывается окно новости или дайджеста бренда."
            />
          }
        >
          {schedRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>День</th>
                    <th>Вид</th>
                    <th>Окно</th>
                    <th>Исход</th>
                    <th>Пост</th>
                    <th>Причина</th>
                  </tr>
                </thead>
                <tbody>
                  {schedRows.map((r) => (
                    <tr key={`${r.kind}/${r.local_day}`}>
                      <td className={styles['numCell']}>{r.local_day}</td>
                      <td>{SCHEDULE_KIND[r.kind] ?? r.kind}</td>
                      <td className={styles['numCell']}>
                        {orDash(r.due_at)} – {orDash(r.until_at)}
                      </td>
                      <td>
                        <StateCell view={SCHEDULE_STATE.get(r.state) ?? UNKNOWN_STATE} />
                      </td>
                      <td className={styles['numCell']}>
                        {r.post_no === null ? '—' : `#${fmtInt(r.post_no)}`}
                      </td>
                      <td className="small">{scheduleReasonText(r.reason)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* ══ ошибки HTTP бэкенда за сутки (0024) ══ */}
        <Section
          id="http-errors"
          title="Ошибки за сутки"
          meta="источник — v_ops_http_errors_daily"
          badge={http5xx === 0 ? null : `${fmtInt(http5xx)} ответов 5xx`}
          alarm={http5xx > 0}
          aside={
            <DataState
              loading={httpErrors.loading}
              error={httpErrors.error}
              empty={httpRows.length === 0}
              emptyText="За сегодня и вчера бэкенд не ответил ни одной ошибкой."
            />
          }
        >
          {httpRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>День</th>
                    <th>Маршрут</th>
                    <th>Код</th>
                    <th>Раз</th>
                    <th>Кто</th>
                    <th>Последний</th>
                  </tr>
                </thead>
                <tbody>
                  {httpRows.map((r) => (
                    <tr key={`${r.day}/${r.listener}/${r.route}/${String(r.status)}/${r.code}`}>
                      <td className={styles['numCell']}>{r.day}</td>
                      <td className={styles['codeCell']}>
                        {r.listener} {r.route}
                      </td>
                      <td className={styles['codeCell']}>
                        {r.status} {r.code}
                      </td>
                      <td className={styles['numCell']}>{fmtInt(r.count)}</td>
                      <td className="small">{r.caller ?? '—'}</td>
                      <td className={styles['numCell']}>
                        <div>{orDash(r.last_at)}</div>
                        {r.last_request_id === null ? null : (
                          <div className="meta">{r.last_request_id}</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* ══ watchdog и доставка алертов ══ */}
        <div className={styles['two']}>
          <Section
            id="watchdog"
            title="Watchdog"
            meta="источник — v_ops_watchdog"
            badge={overdue.length === 0 ? null : `${fmtInt(overdue.length)} просрочено`}
            alarm={overdue.length > 0}
            aside={
              <DataState
                loading={watchdog.loading}
                error={watchdog.error}
                empty={wdRows.length === 0}
                emptyText="Ожиданий не заведено: строка появляется, когда сервис начинает отчитываться о своём цикле."
              />
            }
          >
            {wdRows.length === 0 ? null : (
              <div className="tblwrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Ожидание</th>
                      <th>Состояние</th>
                      <th>Последний раз</th>
                      <th>Ждём до</th>
                      <th>Период</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wdRows.map((r) => (
                      <tr key={r.key}>
                        <td>
                          <div className="small" style={{ color: 'var(--ink)' }}>
                            {r.label}
                          </div>
                          <div className="meta">{r.key}</div>
                        </td>
                        <td>
                          <StateCell view={WATCHDOG_STATE.get(r.state) ?? UNKNOWN_STATE} />
                        </td>
                        <td className={styles['numCell']}>{orDash(r.last_ok_at)}</td>
                        <td className={styles['numCell']}>{orDash(r.expected_by)}</td>
                        <td className={styles['numCell']}>
                          {fmtInt(r.period_sec)} с + {fmtInt(r.grace_sec)} с
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section
            id="alert-delivery"
            title="Доставка алертов"
            meta="источник — v_ops_alert_delivery_daily"
            aside={
              <DataState
                loading={delivery.loading}
                error={delivery.error}
                empty={delRows.length === 0}
                emptyText="Писем ещё не отправлялось: строка появляется на каждую попытку доставки алерта."
              />
            }
          >
            {delRows.length === 0 ? null : (
              <div className="tblwrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>День</th>
                      <th>Транспорт</th>
                      <th>Доставок</th>
                      <th>Успешных</th>
                      <th>Неудачных</th>
                    </tr>
                  </thead>
                  <tbody>
                    {delRows.map((r) => (
                      <tr key={`${r.day ?? '—'}/${r.transport}`}>
                        <td className={styles['numCell']}>{r.day ?? '—'}</td>
                        <td className={styles['codeCell']}>{r.transport}</td>
                        <td className={styles['numCell']}>{fmtInt(r.deliveries)}</td>
                        <td className={styles['numCell']}>{fmtInt(r.sent)}</td>
                        <td className={styles['numCell']}>{fmtInt(r.failed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>

        {/* ══ воронка мониторинга и отсев префильтра ══ */}
        {/*
          Три карточки остаются снаружи: в них живёт тревога 0%/100%, ради которой раздел и
          заведён (llm-costs ПРАВИЛО 3.2). Сворачиваются суточная таблица и пояснение к ней.
        */}
        <Section
          id="prefilter"
          title="Префильтр и воронка обхода"
          meta="источник — v_ops_prefilter_daily"
          cards={
            <div className={styles['kpis3']}>
              <KpiCard
                label="Отсев префильтра"
                value={fmtPct(dropRatio)}
                note={dropNote.note}
                tone={dropNote.tone}
              />
              <KpiCard
                label="Материалов увидено"
                value={preRows.length === 0 ? null : fmtInt(seenTotal)}
                note={
                  preRows.length === 0
                    ? 'прогонов мониторинга ещё не было'
                    : `прошли эвристики: ${fmtInt(heurTotal)}`
                }
              />
              <KpiCard
                label="Тем создано"
                value={preRows.length === 0 ? null : fmtInt(topicsTotal)}
                note={
                  preRows.length === 0
                    ? 'прогонов мониторинга ещё не было'
                    : `дошли до глубокой стадии: ${fmtInt(llmPassedTotal)}`
                }
              />
            </div>
          }
          aside={
            <DataState
              loading={prefilter.loading}
              error={prefilter.error}
              empty={preRows.length === 0}
              emptyText="Прогонов мониторинга ещё не было: воронка наполняется при закрытии первого обхода реестра."
            />
          }
        >
          {preRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>День</th>
                    <th>Прогонов</th>
                    <th>Увидено</th>
                    <th>После эвристик</th>
                    <th>После префильтра</th>
                    <th>Тем</th>
                    <th>Отсев эвристик</th>
                    <th>Отсев префильтра</th>
                  </tr>
                </thead>
                <tbody>
                  {preRows.map((r) => (
                    <tr key={`${r.day ?? '—'}/${r.brand_code ?? '—'}`}>
                      <td className={styles['numCell']}>{r.day ?? '—'}</td>
                      <td className={styles['numCell']}>{fmtInt(r.runs)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.materials_seen)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.passed_heuristic)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.passed_llm)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.topics_created)}</td>
                      <td className={styles['numCell']}>{fmtPct(r.heuristic_drop_ratio) ?? '—'}</td>
                      <td className={styles['numCell']}>{fmtPct(r.prefilter_drop_ratio) ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={styles['tableNote']}>
                <span className="micro">
                  Прочерк в колонке отсева значит «знаменателя не было», а не «отсев нулевой».
                  Отсев, ушедший к 0% или к 100%, — это сломанная рубрика префильтра, а не
                  достижение: в первом случае за неё платит глубокая стадия, во втором тем не будет
                  вовсе.
                </span>
              </div>
            </div>
          )}
        </Section>

        {/* ══ живость источников ══ */}
        <Section
          id="sources"
          title="Живость источников"
          meta="реестр источников с правками из интерфейса; здоровье — по последним попыткам"
          badge={srcBadge}
          alarm={deadCount > 0}
          defaultOpen={deadCount + degradedCount + noYieldCount > 0}
          aside={
            <DataState
              loading={sources.loading}
              error={srcError}
              empty={srcError === undefined && srcRows.length === 0}
              emptyText="Источников в реестре нет: обход реестра включается вместе с сервисом мониторинга (Б1, фаза 2)."
            />
          }
        >
          {srcRows.length === 0 ? null : (
            <>
              {/*
                Цвет несёт ЛЕВАЯ ГРАНИЦА и мягкая подложка, а не заливка: стена сплошного
                красного нечитаема, и на ней перестаёт быть виден единственный мёртвый
                источник среди деградирующих. Цвет берётся по `data-state` из токенов — ни
                одного фирменного цвета литералом (ui-design §2).

                СОСТОЯНИЕ НЕ КОДИРУЕТСЯ ОДНИМ ЦВЕТОМ: слово состояния стоит и в `title`, и
                визуально скрытым текстом внутри ячейки. Иначе ячейка не читается ни
                скринридером, ни человеком, который цвет различает плохо. То же у выключенного:
                «выкл.» — словом, приглушённость ячейки — только ускорение чтения.

                Ячейка — кнопка: Enter и пробел открывают панель так же, как нажатие мышью.
              */}
              <div className={styles['srcGrid']}>
                {srcSorted.map((r) => {
                  const { state: cellState, view } = cellStateOf(r);
                  const touched = r.override.weight !== null || r.override.enabled !== null;
                  const yieldLine = yieldCellText(r);
                  return (
                    <button
                      key={r.source_code}
                      type="button"
                      className={styles['srcCell']}
                      data-state={cellState}
                      data-enabled={r.enabled ? 'true' : 'false'}
                      aria-haspopup="dialog"
                      title={`${r.source_code} — ${view.label}, вес ${weightLabel(r.weight)}${r.enabled ? '' : ', выключен'}${touched ? ', изменён в интерфейсе' : ''}`}
                      onClick={() => {
                        setOpenSource(r.source_code);
                      }}
                    >
                      <span className={styles['srcCode']}>{r.source_code}</span>
                      <span className={styles['srcRatio']}>
                        {r.health.success_ratio_10 === null
                          ? '—'
                          : `${fmtPct(r.health.success_ratio_10) ?? '—'} из ${fmtInt(r.health.attempts_10)}`}
                      </span>
                      {yieldLine === null ? null : (
                        <span className={styles['srcRatio']}>{yieldLine}</span>
                      )}
                      <span className={styles['srcWeight']}>
                        вес {weightLabel(r.weight)}
                        {touched ? ' ✎' : ''}
                        {r.enabled ? '' : ' · выкл.'}
                      </span>
                      {cellStateOf(r).byYield ? (
                        <span className={styles['srcWeight']}>{view.label}</span>
                      ) : null}
                      <span className={styles['srOnly']}>
                        {cellStateOf(r).byYield ? 'лента отвечает' : view.label}
                        {r.enabled ? '' : ', выключен'}
                        {touched ? ', изменён в интерфейсе' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
              <span className="micro">
                Нажмите источник — покажем последнюю попытку, курсор фида, историю обхода и полный
                текст ошибки, а также вес и включение. ✎ — вес или включение изменены здесь, поверх
                реестра.
              </span>

              {/*
                Панель ОДНОГО источника. До 07.09.2026 подробность лежала на экране двадцать
                один раз; с 15.10.2026 она уехала в диалог вместе с ручками веса и включения,
                а её содержимое рисует экран — у него словари исходов и журнал попыток.
              */}
              {shownSource === undefined ? null : (
                <SourcePanel
                  key={shownSource.source_code}
                  brand={brand}
                  source={shownSource}
                  stateLabel={cellStateOf(shownSource).view.label}
                  onSaved={onSourceSaved}
                  onClose={closeSource}
                  health={
                    <>
                      <span className="meta">
                        успешных {fmtPct(shownSource.health.success_ratio_10) ?? '—'} из{' '}
                        {fmtInt(shownSource.health.attempts_10)} последних попыток
                      </span>
                      <div className={styles['srcFacts']}>
                        <span className="meta">
                          последняя попытка: {orDash(shownSource.health.last_attempt_at)}
                        </span>
                        <span className="meta">
                          последний успех: {orDash(shownSource.health.last_success_at)}
                        </span>
                        <span className="meta">
                          курсор фида: {orDash(shownSource.health.cursor_at)}
                        </span>
                      </div>

                      {/*
                        Вторая строка — единственный на экране сигнал «фид жив, а статьи не
                        выкачиваются». Попытки `purpose='article'` исключены из окна здоровья
                        сознательно (0017): шестьдесят успешных статей не должны маскировать
                        мёртвый фид. Обратной сигнализации не было вовсе — вот она.
                      */}
                      <div className="micro">
                        {articleRows.length === 0
                          ? 'статьи: попыток выкачать статью в журнале нет'
                          : `статьи: ${fmtInt(articleOk)} из ${fmtInt(articleRows.length)} скачаны`}
                      </div>

                      {shownSource.health.last_error_text === null ? (
                        <span className="micro">последняя ошибка не записана</span>
                      ) : (
                        <div className={styles['srcError']}>
                          {shownSource.health.last_error_text}
                        </div>
                      )}

                      <DataState
                        loading={fetches.loading}
                        error={fetches.error}
                        empty={sourceFetches.length === 0}
                        emptyText="Журнал попыток по этому источнику пуст: он наполняется каждым обходом реестра."
                      />

                      {sourceFetches.length === 0 ? null : (
                        <div className={styles['srcLog']}>
                          {sourceFetches.slice(0, 10).map((f, i) => (
                            <div
                              key={`${f.source_code}/${String(f.started_at_ms ?? f.started_at ?? i)}/${String(f.attempt_no ?? i)}`}
                              className="meta"
                            >
                              {fetchStamp(f)} · {f.purpose} · {f.outcome}
                              {f.http_status === null ? '' : ` · HTTP ${fmtInt(f.http_status)}`}
                              {f.redirect_location === null ? '' : ` → ${f.redirect_location}`}
                              {f.items_found === null
                                ? ''
                                : ` · элементов ${fmtInt(f.items_found)}, новых ${fmtInt(f.items_new ?? 0)}`}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  }
                />
              )}

              {/*
                Легенда исходов. Список кодов приходит из контрактов (`SOURCE_FETCH_OUTCOMES`,
                он же CHECK на `source_fetch.outcome`) — второго перечня в UI нет; здесь живут
                только русская фраза и то, что с этим делает оператор.
              */}
              <dl className={styles['legend']}>
                {SOURCE_FETCH_OUTCOMES.map((code) => {
                  const view = OUTCOME_LEGEND.get(code);
                  return (
                    <div key={code} className={styles['legendRow']}>
                      <dt className={styles['codeCell']}>{code}</dt>
                      <dd className="micro">
                        {view === undefined ? 'фраза не описана' : view.phrase}
                        {view === undefined || view.action === '' ? '' : ` · ${view.action}`}
                      </dd>
                    </div>
                  );
                })}
              </dl>

              <div className={styles['tableNote']}>
                <span className="micro">
                  Один недоступный источник не блокирует цикл (NFR-4). Вердикт «деградирует»
                  ставится по доле успешных из последних десяти попыток — она стоит в самой ячейке,
                  чтобы решение читалось из своих данных, а не принималось на веру. Сортировка
                  ставит вперёд то, что чинят: мёртвые, деградирующие, ещё не опрошенные,
                  работающие.
                </span>
              </div>
            </>
          )}
        </Section>

        {/* ══ прогоны мониторинга ══ */}
        {/*
          Вьюха и маршрут существуют с 0004, но экран на них подписан не был — а runbook
          «пропущенный прогон» отправляет оператора именно сюда. Раздел свёрнут: это журнал,
          а не показатель, и открывают его по поводу.
        */}
        <Section
          id="runs"
          title="Прогоны мониторинга"
          meta="источник — v_ops_runs"
          badge={runsBad === 0 ? null : `${fmtInt(runsBad)} с отказом`}
          alarm={runsBad > 0}
          aside={
            <DataState
              loading={runs.loading}
              error={runs.error}
              empty={runRows.length === 0}
              emptyText="Прогонов ещё не было: строка появляется на каждый обход реестра и каждую фоновую работу."
            />
          }
        >
          {runRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Начат</th>
                    <th>Завершён</th>
                    <th>Вид</th>
                    <th>Состояние</th>
                    <th>Источники</th>
                    <th>Материалы</th>
                    <th>Тем</th>
                    <th>Класс ошибки</th>
                  </tr>
                </thead>
                <tbody>
                  {runRows.map((r) => (
                    <tr key={r.id}>
                      {/* Обход открывает свою таблицу прогона (docs/release-2026-09-19.md
                          §3.6): `v_ops_runs.id` — это `job_run.id`, он же `monitoring_run.id`. */}
                      <td className={styles['numCell']}>
                        {r.kind === 'monitor' ? (
                          <MonitoringRunOpener
                            brand={brand}
                            runId={r.id}
                            label={orDash(r.started_at)}
                          />
                        ) : (
                          orDash(r.started_at)
                        )}
                      </td>
                      <td className={styles['numCell']}>{orDash(r.finished_at)}</td>
                      <td className={styles['codeCell']}>{RUN_KIND_LABEL.get(r.kind) ?? r.kind}</td>
                      <td>
                        <StateCell view={RUN_STATE.get(r.state) ?? UNKNOWN_STATE} />
                      </td>
                      {/* «N из M» — во вьюхе есть только `sources_ok` и `sources_total`;
                          кто именно мёртв и кто деградирует, отвечает сетка живости выше. */}
                      <td className={styles['numCell']}>
                        {r.sources_total === null
                          ? '—'
                          : `${fmtInt(r.sources_ok ?? 0)} из ${fmtInt(r.sources_total)}`}
                      </td>
                      <td className={styles['numCell']}>
                        {r.materials_seen === null
                          ? '—'
                          : `${fmtInt(r.materials_seen)} → ${fmtInt(r.materials_passed_llm ?? 0)}`}
                      </td>
                      <td className={styles['numCell']}>
                        {r.topics_created === null ? '—' : fmtInt(r.topics_created)}
                      </td>
                      <td className={styles['errCell']}>
                        {r.error_text === null ? (
                          <span className="micro" style={{ color: 'var(--muted)' }}>
                            —
                          </span>
                        ) : (
                          <span className="micro" style={{ color: 'var(--red-ink)' }}>
                            {r.error_text}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={styles['tableNote']}>
                <span className="micro">
                  «Материалы» — сколько увидели и сколько прошло префильтр. Счётчики есть только у
                  прогонов мониторинга: у публикации и обслуживания их нет вовсе, и там стоит
                  прочерк, а не ноль.
                </span>
              </div>
            </div>
          )}
        </Section>

        {/* ══ расход LLM: один заголовок, два разреза, одна таблица ══ */}
        <Section
          id="llm"
          title="Расход LLM"
          meta="источник — v_ops_llm_daily, v_ops_llm_cache_daily"
          cards={
            <div className={styles['kpis3']}>
              <KpiCard
                label="Расход за весь срок"
                value={spendTotal.has ? fmtUsd(spendTotal.sum) : null}
                note={
                  spendTotal.has
                    ? `строк учёта: ${fmtInt(llmRows.length)}`
                    : 'вызовов модели ещё не было'
                }
              />
              <KpiCard
                label="Доля чтений кэша"
                value={fmtPct(cacheRatio)}
                note={cacheNote.note}
                tone={cacheNote.tone}
              />
              <KpiCard
                label="Цена материала вглубь"
                value={fmtUsdFine(deepPerItem)}
                note={
                  deepPerItem === null
                    ? 'нужны и прогон, и учтённый расход'
                    : `материалов вглубь: ${fmtInt(deepItems.sum)}`
                }
              />
            </div>
          }
        >
          {/*
            Переключатель разрезов. Раньше обе таблицы стояли подряд, и это читалось как
            «одно и то же посчитали дважды». Считается разное: по стадиям — что ест бюджет,
            по сервисам — работает ли кэш. Соединить их на клиенте нельзя (см. шапку файла),
            поэтому рисуется РОВНО ОДНА, а строка под переключателем говорит, почему разрезов
            два.
          */}
          <div className="seg" style={{ alignSelf: 'flex-start' }}>
            <button
              type="button"
              className={`seg-o${llmCut === 'stage' ? ' on' : ''}`}
              aria-pressed={llmCut === 'stage'}
              onClick={() => {
                setLlmCut('stage');
              }}
            >
              по стадиям
            </button>
            <button
              type="button"
              className={`seg-o${llmCut === 'cache' ? ' on' : ''}`}
              aria-pressed={llmCut === 'cache'}
              onClick={() => {
                setLlmCut('cache');
              }}
            >
              по кэшу
            </button>
          </div>

          <span className="micro">
            Разрезы отвечают на разные вопросы и не выводятся один из другого: по стадиям видно, что
            ест бюджет, по сервисам — работает ли ещё кэш (кэшируется общий префикс промпта, а он
            заморожен на сервис, а не на стадию). У первого нет колонки сервиса, у второго — стадии,
            поэтому одной таблицы на оба вопроса не существует.
          </span>

          {llmCut === 'stage' ? (
            <DataState
              loading={llm.loading}
              error={llm.error}
              empty={llmRows.length === 0}
              emptyText="Вызовов модели ещё не было: расход, кэш и потолок бюджета наполняются отчётами POST /intake/v1/usage."
            />
          ) : (
            <DataState
              loading={cache.loading}
              error={cache.error}
              empty={cacheRows.length === 0}
              emptyText="Отчётов о кэше ещё не было: разрез наполняется теми же отчётами POST /intake/v1/usage."
            />
          )}

          {llmCut !== 'stage' || llmRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>День</th>
                    <th>Бренд</th>
                    <th>Стадия</th>
                    <th>Вызовов</th>
                    <th>Вход</th>
                    <th>Чтение кэша</th>
                    <th>Выход</th>
                    <th>Стоимость</th>
                  </tr>
                </thead>
                <tbody>
                  {llmRows.map((r) => (
                    <tr key={`${r.day ?? '—'}/${r.brand_code ?? '—'}/${r.stage ?? '—'}`}>
                      <td className={styles['numCell']}>{r.day ?? '—'}</td>
                      <td className={styles['codeCell']}>{r.brand_code ?? '—'}</td>
                      <td className={styles['codeCell']}>{r.stage ?? '—'}</td>
                      <td className={styles['numCell']}>{fmtInt(r.calls)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.input_tokens)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.cache_read_tokens)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.output_tokens)}</td>
                      <td className={styles['numCell']}>{fmtUsd(r.cost_total_micro_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {llmCut !== 'cache' || cacheRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>День</th>
                    <th>Сервис</th>
                    <th>Вызовов</th>
                    <th>Из них с чтением</th>
                    <th>Токенов промпта</th>
                    <th>Из кэша</th>
                    <th>Доля чтений</th>
                  </tr>
                </thead>
                <tbody>
                  {cacheRows.map((r) => (
                    <tr key={`${r.day ?? '—'}/${r.brand_code ?? '—'}/${r.service}`}>
                      <td className={styles['numCell']}>{r.day ?? '—'}</td>
                      <td className={styles['codeCell']}>{r.service}</td>
                      <td className={styles['numCell']}>{fmtInt(r.calls)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.calls_with_cache_read)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.prompt_tokens)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.cache_read_tokens)}</td>
                      <td className={styles['numCell']}>{fmtPct(r.cache_read_ratio) ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={styles['tableNote']}>
                <span className="micro">
                  Ноль в колонке «из них с чтением» при непустом числе вызовов — это регресс кэша, а
                  не мелочь: промах стоит в двадцать раз дороже попадания, и счёт вырастает молча.
                </span>
              </div>
            </div>
          )}
        </Section>

        {/* ══ качество согласования и оценки человека ══ */}
        <Section
          id="quality"
          title="Качество согласования"
          meta="источник — v_ops_quality_daily, v_ops_quality_marks"
          cards={
            <div className={styles['kpis3']}>
              <KpiCard label="С первого раза" value={fmtPct(firstPassRatio)} note={qualNote} />
              <KpiCard
                label="На переделку"
                value={fmtPct(reworkRatio)}
                note={qualNote}
                tone={reworkRatio !== null && reworkRatio > 0 ? 'down' : 'muted'}
              />
              <KpiCard
                label="Оценки постов"
                value={qualRows.length === 0 ? null : `${fmtInt(likes)} / ${fmtInt(dislikes)}`}
                note={qualRows.length === 0 ? 'отметок ещё не ставили' : 'нравится / не нравится'}
              />
            </div>
          }
          aside={
            <DataState
              loading={quality.loading}
              error={quality.error}
              empty={qualRows.length === 0}
              emptyText="Одобрений и отметок ещё не было: доля «с первого раза» считается по решениям человека, а не по номерам версий."
            />
          }
        >
          {qualRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>День</th>
                    <th>Одобрений</th>
                    <th>С первого раза</th>
                    <th>После переделки</th>
                    <th>Доля с первого раза</th>
                    <th>Нравится</th>
                    <th>Не нравится</th>
                  </tr>
                </thead>
                <tbody>
                  {qualRows.map((r) => (
                    <tr key={`${r.day ?? '—'}/${r.brand_code ?? '—'}`}>
                      <td className={styles['numCell']}>{r.day ?? '—'}</td>
                      <td className={styles['numCell']}>{fmtInt(r.approvals)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.first_pass)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.after_rework)}</td>
                      <td className={styles['numCell']}>{fmtPct(r.first_pass_ratio) ?? '—'}</td>
                      <td className={styles['numCell']}>{fmtInt(r.likes)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.dislikes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {markError === null ? null : (
            <div className="val val-err">
              <span>Отметка не сохранилась: {markError}</span>
            </div>
          )}

          <DataState
            loading={marks.loading}
            error={marks.error}
            empty={markRows.length === 0}
            emptyText="Оценивать пока нечего: в список попадает опубликованное — лайк отсюда уходит в базу эталонов."
          />

          {markRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: '44%' }}>Пост</th>
                    <th>Состояние</th>
                    <th>Нравится / нет</th>
                    <th>Отмечен</th>
                    <th>Оценка</th>
                  </tr>
                </thead>
                <tbody>
                  {markRows.map((r) => (
                    <MarkRow
                      key={`${r.draft_id}/${String(r.version_no)}`}
                      row={r}
                      busy={markBusy === `${r.draft_id}/${String(r.version_no)}`}
                      onMark={sendMark}
                    />
                  ))}
                </tbody>
              </table>
              <div className={styles['tableNote']}>
                <span className="micro">
                  Одна отметка на человека и версию: повторное нажатие меняет мнение, а не добавляет
                  второй голос. Эталоны из лайков собирает отдельный экспорт — обратной кнопки
                  «сделать эталоном» здесь нет и не будет.
                </span>
              </div>
            </div>
          )}
        </Section>

        {/* ══ доля постов с картинкой (М5, §2.6) ══ */}
        <Section
          id="visual"
          title="Визуал: доля постов с картинкой"
          meta="источник — v_ops_visual_share"
          cards={
            <div className={styles['kpis3']}>
              <KpiCard
                label="Постов с картинкой"
                value={fmtPct(visualShare)}
                note={visualNote.note}
                tone={visualNote.tone}
              />
              <KpiCard
                label="Вердиктов в выборке"
                value={visualVersions === 0 ? null : fmtInt(visualVersions)}
                note={
                  visualVersions === 0
                    ? 'сервис генерации ещё не выносил вердикта'
                    : 'последние 20 версий от сервиса генерации'
                }
              />
              <KpiCard
                label="Из них нарисовано"
                value={visualVersions === 0 ? null : fmtInt(visualDrawn)}
                note={
                  visualVersions === 0
                    ? 'считать пока нечего'
                    : `отказов: ${fmtInt(visualVersions - visualDrawn)}`
                }
              />
            </div>
          }
          aside={
            <DataState
              loading={visual.loading}
              error={visual.error}
              empty={visualRows.length === 0}
              emptyText="Вердиктов визуала ещё не было: доля появится после первого прогона с включённым визуалом."
            />
          }
        >
          {visualRows.length === 0 ? null : (
            <div className="tblwrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Бренд</th>
                    <th>Вердиктов</th>
                    <th>Нарисовано</th>
                    <th>Доля</th>
                  </tr>
                </thead>
                <tbody>
                  {visualRows.map((r) => (
                    <tr key={r.brand ?? '—'}>
                      <td className={styles['numCell']}>{r.brand ?? '—'}</td>
                      <td className={styles['numCell']}>{fmtInt(r.versions)}</td>
                      <td className={styles['numCell']}>{fmtInt(r.drawn)}</td>
                      <td className={styles['numCell']}>{fmtPct(r.share) ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <span className="micro">
            Доля считается запросом по колонке `draft_version.visual_verdict` за последние 20
            версий, а не отдельным счётчиком: счётчик можно забыть увеличить, колонку — нельзя. Она
            здесь ради одного: решение «рисовать / не рисовать» честно ровно настолько, насколько
            кто-то смотрит на его итог. Цифра, которой никто не видит, ничего не ограничивает.
          </span>
        </Section>
      </div>

      {shownMetric === undefined ? null : (
        <MetricDetail
          metric={shownMetric}
          onClose={() => {
            setOpenMetric(null);
          }}
        />
      )}
    </Shell>
  );
}
